import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { PageHeader, Modal, DetailPanel, ConfirmDialog, Badge, statusBadgeMap, attendanceBadgeMap, sessionTypeBadgeMap, Spinner, EmptyState, useToast, Pagination, SortTh, SearchableSelect, CapacityResolveModal, intakeOptionsFor, InlineSpinner, TableSkeletonRows, FlagIcon } from '../components/UI';
import { usePermissions } from '../context/PermissionsContext';
import { fmtDate, fmtDateTime, fmtSessionName, fmtPct } from '../utils/format';
import FileAttachments from '../components/FileAttachments';
import ProfilePicture from '../components/ProfilePicture';

// Display-only formatting — some fields (gender, citizenship) are stored
// ALL CAPS to match their picklist options, but should read as Title Case
// in the details view.
function displayTitleCase(val) {
  if (!val) return val;
  return String(val).trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// The countries table has one row per (country, nationality) pair — e.g.
// United Kingdom appears once per demonym (English, Scottish, Welsh, ...) —
// so a dropdown keyed on just one of those columns needs de-duping or it
// repeats the same label several times.
function distinctLabels(values) {
  return Array.from(new Set(values.filter(Boolean))).map(v => ({ value: v, label: v }));
}

// Live as-you-type formatters for free-text fields — no .trim() here, so a
// trailing space (e.g. typing a second name) isn't eaten mid-keystroke.
function liveTitleCase(val) {
  return val.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}
function liveLowerCase(val) {
  return val.toLowerCase();
}

function canonicalGuidingLanguage(val) {
  const value = String(val || '').trim().replace(/\s+/g, ' ');
  return /^bashasa indonesia$/i.test(value) ? 'Bahasa Indonesia' : value;
}

function guidingLanguageKey(val) {
  return canonicalGuidingLanguage(val).toLowerCase();
}

// Green/amber/red thresholds, always drawn from the app's WCAG-AA-verified
// --sms-dot-* tokens (index.css) rather than raw hex, so contrast holds in
// both light and dark mode.
function pctColorVar(pct) {
  if (pct >= 85) return 'var(--sms-dot-present)';
  if (pct >= 70) return 'var(--sms-dot-late)';
  return 'var(--sms-dot-absent)';
}

// Same thresholds, but as a tinted pill (badge-present/late/absent — the
// same classes attendanceBadgeMap uses, background+text pair verified
// WCAG AA in both themes in index.css) instead of plain colored text.
function pctBadgeClass(pct) {
  if (pct >= 85) return 'badge-present';
  if (pct >= 70) return 'badge-late';
  return 'badge-absent';
}

function fmtAmount(value) {
  const n = Number(value);
  if (isNaN(n)) return '—';
  return `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PAYMENT_METHOD_LABELS = {
  cash: 'Cash', bank_transfer: 'Bank Transfer', credit_card: 'Credit Card', cheque: 'Cheque', paynow: 'PayNow',
};
// badge-paid/badge-void — same WCAG-AA-verified pair ReceiptsPage uses (index.css).
const RECEIPT_STATUS_LABELS = { paid: 'Paid', void: 'Void' };

function IntakeMultiSelect({ intakes, selected, onChange }) {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const containerRef            = useRef(null);
  const inputRef                = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = intakes.filter(i =>
    i.intake_code.toLowerCase().includes(query.toLowerCase())
  );

  const toggle = useCallback(code => {
    onChange(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  }, [onChange]);

  const openDropdown = () => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Selected chips + search trigger */}
      <div
        className="sms-form-control"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 4, cursor: 'text', minHeight: 42, height: 'auto', padding: '4px 8px', alignItems: 'center' }}
        onClick={openDropdown}
      >
        {selected.map(code => (
          <span key={code} style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            background: 'var(--sms-acc-bg)', color: 'var(--sms-acc)',
            border: '1px solid var(--sms-acc)', borderRadius: 5,
            fontSize: 12, fontWeight: 600, padding: '1px 6px', whiteSpace: 'nowrap',
          }}>
            {code}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); toggle(code); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', lineHeight: 1, fontSize: 12 }}
              aria-label={`Remove ${code}`}
            >×</button>
          </span>
        ))}
        {selected.length === 0 && !open && (
          <span style={{ color: 'var(--sms-txt3)', fontSize: 13 }}>All intakes</span>
        )}
        {open && (
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search intake…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--sms-txt)', flex: 1, minWidth: 80 }}
            onClick={e => e.stopPropagation()}
          />
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400,
          background: 'var(--sms-sur)', border: '1px solid var(--sms-bdr)',
          borderRadius: 8, boxShadow: 'var(--sms-shadow-dd)',
          maxHeight: 220, overflowY: 'auto', marginTop: 4,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--sms-txt3)' }}>No intakes found</div>
          ) : filtered.map(i => {
            const checked = selected.includes(i.intake_code);
            return (
              <label key={i.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', cursor: 'pointer', fontSize: 13,
                color: checked ? 'var(--sms-acc)' : 'var(--sms-txt)',
                background: checked ? 'var(--sms-acc-bg)' : 'transparent',
                transition: 'background .1s',
              }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = 'var(--sms-sur2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = checked ? 'var(--sms-acc-bg)' : 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(i.intake_code)}
                  style={{ accentColor: 'var(--sms-acc)', width: 14, height: 14, flexShrink: 0 }}
                />
                {i.intake_code}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Headline status card — tinted gradient + accent-colored icon badge (same
// treatment as the dashboard's StatCard), riding on the same sms-rate-card/
// sms-rate-icon hover animation ClassPassingRatePage's cards use (lift, glow
// shadow, accent-colored border, top bar wiping in, icon popping) instead of
// sitting there flat. Also gets a lit-up ring while its status is the active
// table filter, so toggling it on reads clearly instead of just changing the
// table underneath.
function ProfileStatCard({ label, value, icon, color, active, onClick }) {
  return (
    <div
      className="sms-stat-card sms-rate-card" role="button" tabIndex={0}
      style={{
        cursor: 'pointer',
        background: `linear-gradient(160deg, color-mix(in srgb, ${color} ${active ? 16 : 9}%, var(--sms-sur)), var(--sms-sur) 70%)`,
        '--card-accent': color,
        ...(active ? { borderColor: color, boxShadow: `0 0 0 1px ${color}, var(--sms-shadow)` } : {}),
      }}
      onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div className="sms-rate-icon" style={{ width: 34, height: 34, borderRadius: 9, background: `color-mix(in srgb, ${color} 18%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`bi ${icon}`} style={{ color, fontSize: 16 }} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

const EMPTY = { name:'', title:'', nric:'', identity_type:'', gender:'', citizenship:'', nationality:'', date_of_birth:'', race:'', email:'', phone:'', contact_no_country_code:'', intake:'', language:'', guiding_language:'', education_level:'', employment_designation:'', employment_salary_range:'', next_of_kin:'', next_of_kin_contact:'', type_of_application:'', enrollment_date:'', status:'registered', remarks:'', notify_email:true, notify_whatsapp:false };

// The 11 applicant-only fields real import files frequently leave blank —
// mirrors REVIEW_FIELDS in server/controllers/students.controller.js. The
// import review modal highlights blanks in these fields and blocks
// "Import all" until every one is filled in — same treatment now applies to
// every other required student field too (see CORE_REVIEW_FIELDS below),
// since real files just as often leave core fields like Nationality blank.
const TITLE_OPTIONS = ['Prof', 'Dr', 'Mdm', 'Ms', 'Mr', 'Mrs'];
const IDENTITY_TYPE_OPTIONS = ['NRIC', 'FIN', 'Passport'];
const TYPE_OF_APPLICATION_OPTIONS = ['Self Registered', 'Company Registered'];
// A freshly-imported applicant only ever starts out registered or (if
// already attending) active — mirrors IMPORT_STATUS_OPTIONS server-side.
const IMPORT_STATUS_OPTIONS = ['registered', 'active'];
// Values match what normalizeStudentPayload() (server-side) upper/title-cases
// them to, so the picked option actually matches the row's stored value.
const IMPORT_GENDER_OPTIONS = [{ value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' }];
const IMPORT_LANGUAGE_OPTIONS = ['English', 'Chinese'];
const GUIDING_LANGUAGE_OPTIONS = ['Bahasa Indonesia', 'Chinese', 'English', 'French', 'Japanese', 'Korean', 'Polish', 'Russian', 'Vietnamese'];
const GUIDING_LANGUAGE_OTHER = 'Others';
// Citizenship in this app was previously just "country of origin", but real
// applicant files (and everyday SG usage) treat it as a residency status —
// Singaporean or Singapore PR, with a country only relevant for foreigners.
// Prepended to the plain country list so both statuses are selectable
// everywhere Citizenship appears (import review below, and the Add/Edit
// Student form), not just for freshly-imported applicants.
const CITIZENSHIP_EXTRA_OPTIONS = ['Singaporean', 'Singapore PR'];
function citizenshipOptionsFor(countries) {
  return [
    ...CITIZENSHIP_EXTRA_OPTIONS.map(v => ({ value: v, label: v })),
    // Plain "Singapore" is superseded by Singaporean/Singapore PR above —
    // keeping it too would just be a confusing third, redundant option.
    ...distinctLabels(countries.map(c => displayTitleCase(c.country)).filter(c => c !== 'Singapore')),
  ];
}
// A value from the file (e.g. a demonym or abbreviation the option list
// doesn't have) can be non-blank yet match nothing in `options` — the
// SearchableSelect then silently shows its placeholder instead of the
// value, which used to read as "filled in" to the blank-check even though
// nothing valid was actually selected. This checks real membership instead
// of just truthiness, so that case is treated as blank like any other.
function isValidOption(value, options) {
  if (!value) return false;
  return options.some(o => (typeof o === 'string' ? o : o.value) === value);
}

// ITU/E.164 country calling codes — mirrors COUNTRY_CALLING_CODES
// (server-side, students.controller.js), which blanks out anything the file
// provides that isn't on this list so the reviewer picks a real one here
// instead of importing an unverifiable value.
// { value: calling code, iso2: flag country code(s), name: label text }.
// iso2 can be a space-separated pair (e.g. "US CA") for a code shared by
// more than one country — each gets its own flag, shown side by side.
const COUNTRY_CALLING_CODE_DATA = [
  { value: '1',   iso2: 'US CA', name: 'United States / Canada' },
  { value: '7',   iso2: 'RU KZ', name: 'Russia / Kazakhstan' },
  { value: '20',  iso2: 'EG', name: 'Egypt' },
  { value: '27',  iso2: 'ZA', name: 'South Africa' },
  { value: '30',  iso2: 'GR', name: 'Greece' },
  { value: '31',  iso2: 'NL', name: 'Netherlands' },
  { value: '32',  iso2: 'BE', name: 'Belgium' },
  { value: '33',  iso2: 'FR', name: 'France' },
  { value: '34',  iso2: 'ES', name: 'Spain' },
  { value: '36',  iso2: 'HU', name: 'Hungary' },
  { value: '39',  iso2: 'IT', name: 'Italy' },
  { value: '40',  iso2: 'RO', name: 'Romania' },
  { value: '41',  iso2: 'CH', name: 'Switzerland' },
  { value: '43',  iso2: 'AT', name: 'Austria' },
  { value: '44',  iso2: 'GB', name: 'United Kingdom' },
  { value: '45',  iso2: 'DK', name: 'Denmark' },
  { value: '46',  iso2: 'SE', name: 'Sweden' },
  { value: '47',  iso2: 'NO', name: 'Norway' },
  { value: '48',  iso2: 'PL', name: 'Poland' },
  { value: '49',  iso2: 'DE', name: 'Germany' },
  { value: '51',  iso2: 'PE', name: 'Peru' },
  { value: '52',  iso2: 'MX', name: 'Mexico' },
  { value: '53',  iso2: 'CU', name: 'Cuba' },
  { value: '54',  iso2: 'AR', name: 'Argentina' },
  { value: '55',  iso2: 'BR', name: 'Brazil' },
  { value: '56',  iso2: 'CL', name: 'Chile' },
  { value: '57',  iso2: 'CO', name: 'Colombia' },
  { value: '58',  iso2: 'VE', name: 'Venezuela' },
  { value: '60',  iso2: 'MY', name: 'Malaysia' },
  { value: '61',  iso2: 'AU', name: 'Australia' },
  { value: '62',  iso2: 'ID', name: 'Indonesia' },
  { value: '63',  iso2: 'PH', name: 'Philippines' },
  { value: '64',  iso2: 'NZ', name: 'New Zealand' },
  { value: '65',  iso2: 'SG', name: 'Singapore' },
  { value: '66',  iso2: 'TH', name: 'Thailand' },
  { value: '81',  iso2: 'JP', name: 'Japan' },
  { value: '82',  iso2: 'KR', name: 'South Korea' },
  { value: '84',  iso2: 'VN', name: 'Vietnam' },
  { value: '86',  iso2: 'CN', name: 'China' },
  { value: '90',  iso2: 'TR', name: 'Turkey' },
  { value: '91',  iso2: 'IN', name: 'India' },
  { value: '92',  iso2: 'PK', name: 'Pakistan' },
  { value: '93',  iso2: 'AF', name: 'Afghanistan' },
  { value: '94',  iso2: 'LK', name: 'Sri Lanka' },
  { value: '95',  iso2: 'MM', name: 'Myanmar' },
  { value: '98',  iso2: 'IR', name: 'Iran' },
  { value: '211', iso2: 'SS', name: 'South Sudan' },
  { value: '212', iso2: 'MA', name: 'Morocco' },
  { value: '213', iso2: 'DZ', name: 'Algeria' },
  { value: '216', iso2: 'TN', name: 'Tunisia' },
  { value: '218', iso2: 'LY', name: 'Libya' },
  { value: '220', iso2: 'GM', name: 'Gambia' },
  { value: '221', iso2: 'SN', name: 'Senegal' },
  { value: '233', iso2: 'GH', name: 'Ghana' },
  { value: '234', iso2: 'NG', name: 'Nigeria' },
  { value: '237', iso2: 'CM', name: 'Cameroon' },
  { value: '241', iso2: 'GA', name: 'Gabon' },
  { value: '250', iso2: 'RW', name: 'Rwanda' },
  { value: '251', iso2: 'ET', name: 'Ethiopia' },
  { value: '254', iso2: 'KE', name: 'Kenya' },
  { value: '255', iso2: 'TZ', name: 'Tanzania' },
  { value: '256', iso2: 'UG', name: 'Uganda' },
  { value: '260', iso2: 'ZM', name: 'Zambia' },
  { value: '263', iso2: 'ZW', name: 'Zimbabwe' },
  { value: '264', iso2: 'NA', name: 'Namibia' },
  { value: '266', iso2: 'LS', name: 'Lesotho' },
  { value: '267', iso2: 'BW', name: 'Botswana' },
  { value: '268', iso2: 'SZ', name: 'Eswatini' },
  { value: '351', iso2: 'PT', name: 'Portugal' },
  { value: '352', iso2: 'LU', name: 'Luxembourg' },
  { value: '353', iso2: 'IE', name: 'Ireland' },
  { value: '354', iso2: 'IS', name: 'Iceland' },
  { value: '355', iso2: 'AL', name: 'Albania' },
  { value: '356', iso2: 'MT', name: 'Malta' },
  { value: '357', iso2: 'CY', name: 'Cyprus' },
  { value: '358', iso2: 'FI', name: 'Finland' },
  { value: '359', iso2: 'BG', name: 'Bulgaria' },
  { value: '370', iso2: 'LT', name: 'Lithuania' },
  { value: '371', iso2: 'LV', name: 'Latvia' },
  { value: '372', iso2: 'EE', name: 'Estonia' },
  { value: '373', iso2: 'MD', name: 'Moldova' },
  { value: '374', iso2: 'AM', name: 'Armenia' },
  { value: '375', iso2: 'BY', name: 'Belarus' },
  { value: '376', iso2: 'AD', name: 'Andorra' },
  { value: '377', iso2: 'MC', name: 'Monaco' },
  { value: '380', iso2: 'UA', name: 'Ukraine' },
  { value: '381', iso2: 'RS', name: 'Serbia' },
  { value: '385', iso2: 'HR', name: 'Croatia' },
  { value: '386', iso2: 'SI', name: 'Slovenia' },
  { value: '420', iso2: 'CZ', name: 'Czech Republic' },
  { value: '421', iso2: 'SK', name: 'Slovakia' },
  { value: '423', iso2: 'LI', name: 'Liechtenstein' },
  { value: '673', iso2: 'BN', name: 'Brunei' },
  { value: '675', iso2: 'PG', name: 'Papua New Guinea' },
  { value: '679', iso2: 'FJ', name: 'Fiji' },
  { value: '852', iso2: 'HK', name: 'Hong Kong' },
  { value: '853', iso2: 'MO', name: 'Macau' },
  { value: '855', iso2: 'KH', name: 'Cambodia' },
  { value: '856', iso2: 'LA', name: 'Laos' },
  { value: '880', iso2: 'BD', name: 'Bangladesh' },
  { value: '886', iso2: 'TW', name: 'Taiwan' },
  { value: '960', iso2: 'MV', name: 'Maldives' },
  { value: '961', iso2: 'LB', name: 'Lebanon' },
  { value: '962', iso2: 'JO', name: 'Jordan' },
  { value: '963', iso2: 'SY', name: 'Syria' },
  { value: '964', iso2: 'IQ', name: 'Iraq' },
  { value: '965', iso2: 'KW', name: 'Kuwait' },
  { value: '966', iso2: 'SA', name: 'Saudi Arabia' },
  { value: '967', iso2: 'YE', name: 'Yemen' },
  { value: '968', iso2: 'OM', name: 'Oman' },
  { value: '970', iso2: 'PS', name: 'Palestine' },
  { value: '971', iso2: 'AE', name: 'United Arab Emirates' },
  { value: '972', iso2: 'IL', name: 'Israel' },
  { value: '973', iso2: 'BH', name: 'Bahrain' },
  { value: '974', iso2: 'QA', name: 'Qatar' },
  { value: '975', iso2: 'BT', name: 'Bhutan' },
  { value: '976', iso2: 'MN', name: 'Mongolia' },
  { value: '977', iso2: 'NP', name: 'Nepal' },
  { value: '992', iso2: 'TJ', name: 'Tajikistan' },
  { value: '993', iso2: 'TM', name: 'Turkmenistan' },
  { value: '994', iso2: 'AZ', name: 'Azerbaijan' },
  { value: '995', iso2: 'GE', name: 'Georgia' },
  { value: '996', iso2: 'KG', name: 'Kyrgyzstan' },
  { value: '998', iso2: 'UZ', name: 'Uzbekistan' },
];
// Singapore first (this school's home country, and the calling code every
// other student most often needs), then the rest ordered by calling code.
const COUNTRY_CALLING_CODES = [...COUNTRY_CALLING_CODE_DATA]
  .sort((a, b) => (a.value === '65' ? -1 : b.value === '65' ? 1 : Number(a.value) - Number(b.value)))
  .map(({ value, iso2, name }) => ({
    value,
    label: `+${value} ${name}`,
    icon: (
      <span style={{ display: 'flex', gap: 2 }}>
        {iso2.split(' ').map(code => <FlagIcon key={code} iso2={code} />)}
      </span>
    ),
  }));

// Singapore NRIC/FIN share the same shape (letter + 7 digits + letter) but
// NRIC is only ever issued with an S/T prefix (citizens/PR) and FIN with
// F/G/M (foreigners) — validated separately so picking the wrong Identity
// Type for a given number is actually caught. Passport numbers have no
// universal format across countries, so this only checks a plausible
// length/charset rather than a strict pattern.
const NRIC_REGEX = /^[ST]\d{7}[A-Z]$/i;
const FIN_REGEX = /^[FGM]\d{7}[A-Z]$/i;
const PASSPORT_REGEX = /^[A-Z0-9]{6,12}$/i;
function identityValueError(identityType, value) {
  const v = String(value || '').trim();
  if (!v) return 'Required';
  if (identityType === 'NRIC')     return NRIC_REGEX.test(v)     ? '' : 'Invalid NRIC (e.g. S1234567A)';
  if (identityType === 'FIN')      return FIN_REGEX.test(v)      ? '' : 'Invalid FIN (e.g. G1234567A)';
  if (identityType === 'Passport') return PASSPORT_REGEX.test(v) ? '' : 'Must be 6-12 letters/numbers';
  return '';
}

// The 11 applicant-only fields (no home before the Phase-1 migration).
const REVIEW_FIELDS = [
  { key: 'title',                   label: 'Title',                   type: 'select', options: TITLE_OPTIONS },
  { key: 'identity_type',           label: 'Identity Type',           type: 'select', options: IDENTITY_TYPE_OPTIONS },
  { key: 'date_of_birth',           label: 'Date of Birth',           type: 'date' },
  { key: 'race',                    label: 'Race',                    type: 'text' },
  { key: 'contact_no_country_code', label: 'Contact No. Country Code',type: 'text' },
  { key: 'education_level',         label: 'Education Level',         type: 'text' },
  { key: 'employment_designation',  label: 'Employment Designation',  type: 'text' },
  { key: 'employment_salary_range', label: 'Employment Salary Range', type: 'text' },
  { key: 'next_of_kin',             label: 'Next-of-Kin',             type: 'text' },
  { key: 'next_of_kin_contact',     label: 'Next-of-Kin Contact',     type: 'text' },
  { key: 'type_of_application',     label: 'Type Of Application',     type: 'select', options: TYPE_OF_APPLICATION_OPTIONS },
];
// Every other field that was already part of a student record before Phase
// 1 — Citizenship/Nationality are handled separately in the card (need the
// live `countries` list), Name/Identity Value/Course/Intake/Status likewise
// (custom validation or filtering), so this only covers the plain ones.
const CORE_REVIEW_FIELDS = [
  { key: 'gender',           label: 'Gender',           type: 'select', options: IMPORT_GENDER_OPTIONS },
  { key: 'email',            label: 'Email',            type: 'email' },
  { key: 'phone',            label: 'Phone',            type: 'tel' },
  { key: 'language',         label: 'Language',         type: 'select', options: IMPORT_LANGUAGE_OPTIONS },
  { key: 'guiding_language', label: 'Guiding Language', type: 'select', options: GUIDING_LANGUAGE_OPTIONS },
  { key: 'enrollment_date',  label: 'Enrolment Date',   type: 'date' },
];
// Purely decorative — a quick visual anchor per field in the review card,
// same Tabler-free Bootstrap Icons set already used everywhere else on this
// page (see the "bi bi-*" classes throughout).
const REVIEW_FIELD_ICONS = {
  name: 'bi-person', title: 'bi-tag', identity_type: 'bi-card-text', nric: 'bi-fingerprint',
  gender: 'bi-gender-ambiguous', citizenship: 'bi-flag', nationality: 'bi-globe-americas',
  date_of_birth: 'bi-calendar-event', race: 'bi-people', email: 'bi-envelope', phone: 'bi-telephone',
  contact_no_country_code: 'bi-globe', course: 'bi-mortarboard', intake: 'bi-calendar-range',
  status: 'bi-clipboard-check', language: 'bi-translate', guiding_language: 'bi-chat-dots',
  enrollment_date: 'bi-calendar-plus', education_level: 'bi-book', employment_designation: 'bi-briefcase',
  employment_salary_range: 'bi-cash-coin', next_of_kin: 'bi-people-fill', next_of_kin_contact: 'bi-telephone-plus',
  type_of_application: 'bi-file-earmark-text',
};
function RFLabel({ icon, text }) {
  return (
    <span>
      {icon && <i className={`bi ${icon}`} style={{ marginRight: 6, color: 'var(--sms-txt3)', fontSize: 12 }} aria-hidden="true" />}
      {text}
    </span>
  );
}

const fmtSessionTime = value => {
  if (!value) return '—';
  const parts = String(value).split(':');
  return parts.length >= 2
    ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
    : String(value);
};
const sessionTypeKey = value => String(value || '').trim().toLowerCase();

function CopyPhoneButton({ onCopy }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label="Copy phone number"
      title="Copy phone number"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', padding: 4, color: 'var(--sms-acc)',
        cursor: 'pointer', lineHeight: 1, flexShrink: 0,
      }}
    >
      <i className="bi bi-copy" aria-hidden="true" />
    </button>
  );
}

// Enrolment-year filter range — 2025 is the earliest cohort year the Student
// Profile page needs to filter back to.
const MIN_ENROLMENT_YEAR = 2025;
const CURRENT_YEAR = new Date().getFullYear();
const ENROLMENT_YEARS = Array.from(
  { length: Math.max(CURRENT_YEAR - MIN_ENROLMENT_YEAR + 1, 1) },
  (_, i) => CURRENT_YEAR - i
); // descending — most recent year first

const dateRange = (soc, eoc) => `${fmtDate(soc)} – ${fmtDate(eoc)}`;

export default function StudentsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const fileRef = useRef();
  const attachRef = useRef();
  const pictureRef = useRef();

  const copyPhone = async phone => {
    const value = String(phone || '').trim();
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast('Phone number copied', 'success');
    } catch {
      toast('Unable to copy phone number', 'error');
    }
  };

  const [students,     setStudents]     = useState([]); // current page's rows only — see load()
  const [serverTotal,  setServerTotal]  = useState(0);   // filtered count, from the server
  const [statusCounts, setStatusCounts] = useState({ registered: 0, active: 0, inactive: 0, graduated: 0, withdrawn: 0, deferred: 0, all_time_total: 0 });
  const [intakes,      setIntakes]      = useState([]);
  const [courses,      setCourses]      = useState([]);
  const [countries,    setCountries]    = useState([]);
  const [savedGuidingLanguages, setSavedGuidingLanguages] = useState([]);
  const [loading,      setLoading]      = useState(true);
  // Table-level filters
  const [search,        setSearch]        = useState('');
  // Debounced so typing a search term doesn't fire a request per keystroke.
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);
  const [filterIntakes, setFilterIntakes] = useState([]);
  const [filterStatus,  setFilterStatus]  = useState('');
  // "Needs Modules" toggle — students whose active/registered course has no
  // modules set up yet (the amber dot next to the row number).
  const [filterNeedsModules, setFilterNeedsModules] = useState(false);
  // Enrolment-year filter — selectable range starts at MIN_ENROLMENT_YEAR
  // (the earliest cohort data goes back to), defaults to the current year.
  const [filterYear,    setFilterYear]    = useState(CURRENT_YEAR);
  // Clicking the currently-active status card again clears the table instead
  // of falling back to "show every status" — clicking that same card (or a
  // different one) after that goes back to retrieving results as normal.
  const [statusCleared, setStatusCleared] = useState(false);
  // Form
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(EMPTY);
  const [guidingLanguageSelection, setGuidingLanguageSelection] = useState('');
  // Course is a client-side filter aid for the Intake dropdown only — the
  // student record itself just stores intake_code, same as before.
  const [formCourse, setFormCourse] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [formError,  setFormError]  = useState('');
  const [fieldErrors,setFieldErrors]= useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [importing,  setImporting]  = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { current, total, fileName } while importing multiple files
  const [importResult, setImportResult] = useState(null); // full /students/import/confirm response, shown in a results modal
  const [importPreview, setImportPreview] = useState(null); // { rows: [...] } from /students/import/preview, shown in the review modal
  const [importConfirming, setImportConfirming] = useState(false);
  const [capacityLeftover, setCapacityLeftover] = useState([]);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [mismatchLeftover, setMismatchLeftover] = useState([]);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [viewing,    setViewing]    = useState(null);
  // Drawer visibility, separate from `viewing` — `viewing` is never nulled on
  // close (only replaced by the next row clicked), so DetailPanel still has
  // data to render while it plays its slide-out animation.
  const [viewingOpen, setViewingOpen] = useState(false);
  // Details-modal collapsible sections — collapsed by default, fetched on
  // first expand only (not eagerly on modal open).
  const [sections, setSections] = useState({
    attendance: { open: false, loading: false, data: null },
    academic:   { open: false, loading: false, data: null },
    mcq:        { open: false, loading: false, data: null },
    payments:   { open: false, loading: false, data: null },
  });

  // Course enrolment history for the student currently open in the details modal.
  const [studentCourses,   setStudentCourses]   = useState([]);
  const [intakesLoading,   setIntakesLoading]   = useState(false);
  const [intakeRates,      setIntakeRates]      = useState({});
  const [historyFor,       setHistoryFor]       = useState(null); // row id whose status history is expanded
  const [historyRows,      setHistoryRows]      = useState([]);
  const [historyLoading,   setHistoryLoading]   = useState(false);
  const [enrollOpen,       setEnrollOpen]       = useState(false);
  const [enrollForm,       setEnrollForm]       = useState({ course_code: '', intake_code: '' });
  const [transferTarget,   setTransferTarget]   = useState(null); // the student_courses row being transferred
  const [transferForm,     setTransferForm]     = useState({ course_code: '', to_intake_code: '', transfer_date: '', attendance_migration: 'from_date' });
  const [transferErrors,   setTransferErrors]   = useState({});
  const [intakeActionBusy, setIntakeActionBusy] = useState(false);

  // "Add Module" — backfills one module (already assigned to the intake)
  // onto a single already-enrolled student's own module list, for the case
  // where a module gets added to an intake after some students are already
  // in it and only specific students need catching up.
  const [moduleTarget,     setModuleTarget]     = useState(null); // the student_courses row the modal is open for
  const [enrolmentModules, setEnrolmentModules] = useState([]);
  const [modulesLoading,   setModulesLoading]   = useState(false);
  const [addingModule,     setAddingModule]     = useState(null); // module_code currently being added

  // Attendance Rate section: lets staff pick which of the student's
  // enrolments (and optionally which module within it) to view the rate for,
  // instead of only ever showing the primary enrolment.
  const [attendanceIntake,  setAttendanceIntake]  = useState('');
  const [attendanceModule,  setAttendanceModule]  = useState('');
  const [attendanceModules, setAttendanceModules] = useState([]);

  // Table paging/sorting — now server-side (the table can have 10,000+ rows,
  // so fetching everything and slicing it in the browser doesn't scale; see
  // MAX_PAGE_SIZE below for why "All" doesn't mean "all of them").
  const [page,     setPage]     = useState(1);
  const [pageSize, rawSetPageSize] = useState(20);
  const [sort,     setSort]     = useState({ key: 'student_no', dir: 'desc' });
  const toggleSort = key => setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  // The shared Pagination component's page-size dropdown includes an "All"
  // option (sentinel value 999999) meant for small tables. Letting that
  // through here would ask the server for 10,000+ rows in one response —
  // exactly what server-side paging exists to avoid — so it's capped to the
  // same limit students.controller.js clamps pageSize to server-side.
  const MAX_PAGE_SIZE = 200;
  const setPageSize = size => rawSetPageSize(Math.min(size, MAX_PAGE_SIZE));

  // Keep fixed choices alphabetized, add values already saved for students,
  // and keep the custom-value choice at the bottom of the dropdown.
  const guidingLanguageValues = Array.from(
    [...GUIDING_LANGUAGE_OPTIONS, ...savedGuidingLanguages].reduce((options, rawValue) => {
      const value = canonicalGuidingLanguage(rawValue);
      const key = guidingLanguageKey(value);
      if (value && key !== GUIDING_LANGUAGE_OTHER.toLowerCase() && !options.has(key)) options.set(key, value);
      return options;
    }, new Map()).values()
  ).sort((a, b) => a.localeCompare(b));
  const guidingLanguageOptions = [...guidingLanguageValues, GUIDING_LANGUAGE_OTHER];

  const load = async () => {
    // A re-click of the active status card asks for an intentionally empty
    // table — skip the request entirely rather than fetch something we're
    // just going to throw away.
    if (statusCleared) { setStudents([]); setServerTotal(0); return; }
    setLoading(true);
    try {
      const res = await api.get('/students', { params: {
        page, pageSize,
        search: searchDebounced || undefined,
        status: filterStatus || undefined,
        intakes: filterIntakes.length ? filterIntakes.join(',') : undefined,
        year: filterYear,
        needsModules: filterNeedsModules ? '1' : undefined,
        sortKey: sort.key, sortDir: sort.dir,
      } });
      setStudents(res.data.data);
      setServerTotal(res.data.total ?? res.data.data.length);
    }
    catch { toast('Failed to load students','error'); }
    finally { setLoading(false); }
  };

  const loadStatusCounts = () => {
    api.get('/students/status-counts', { params: { year: filterYear } }).then(r => setStatusCounts(r.data.data)).catch(() => {});
  };

  useEffect(() => { api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/courses').then(r => setCourses(r.data.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/countries').then(r => setCountries(r.data.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get('/students/guiding-languages')
      .then(r => setSavedGuidingLanguages(Array.isArray(r.data.data) ? r.data.data : []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadStatusCounts(); }, [filterYear]);
  // Any filter/sort/page-size change should jump back to page 1 — a leftover
  // page 4 from a previous, larger result set would otherwise just render empty.
  useEffect(() => { setPage(1); }, [searchDebounced, filterStatus, statusCleared, filterIntakes.join(','), filterYear, filterNeedsModules, pageSize, sort.key, sort.dir]);
  useEffect(() => { load(); }, [page, pageSize, searchDebounced, filterStatus, statusCleared, filterIntakes.join(','), filterYear, filterNeedsModules, sort.key, sort.dir]);

  // Collapse and clear the detail sections whenever a different student
  // (or none) is opened in the details modal.
  useEffect(() => {
    setSections({
      attendance: { open: false, loading: false, data: null },
      academic:   { open: false, loading: false, data: null },
      mcq:        { open: false, loading: false, data: null },
      payments:   { open: false, loading: false, data: null },
    });
    setEnrollOpen(false);
    setEnrollForm({ course_code: '', intake_code: '' });
    setHistoryFor(null);
    setHistoryRows([]);
    setModuleTarget(null);
    setEnrolmentModules([]);
    setAttendanceIntake(viewing?.intake || '');
    setAttendanceModule('');
    if (viewing) loadStudentCourses(viewing.student_no);
    else setStudentCourses([]);
  }, [viewing]);

  const loadStudentCourses = async studentNo => {
    setIntakesLoading(true);
    setIntakeRates({});
    try {
      const res = await api.get(`/students/${studentNo}/courses`);
      const rows = res.data.data;
      setStudentCourses(rows);
      // Each course enrolment can have its own attendance history — fetch a rate per row
      // so a student with more than one enrolment doesn't only ever show a single rate.
      const rates = {};
      await Promise.all(rows.map(async row => {
        try {
          const r = await api.get(`/attendance/student/${studentNo}`, { params: { intake_code: row.intake_code } });
          const s = r.data.data?.summary;
          const totalSessions = s ? Number(s.total_sessions) : 0;
          const present = Number(s?.present) || 0;
          const late = Number(s?.late) || 0;
          const absent = Number(s?.absent) || 0;
          const mc = Number(s?.mc) || 0;
          rates[row.intake_code] = totalSessions > 0
            ? Math.round((((present + late + mc) - absent) / totalSessions) * 100)
            : null;
        } catch { rates[row.intake_code] = null; }
      }));
      setIntakeRates(rates);
    } catch {
      toast('Failed to load enrolment history', 'error');
    } finally {
      setIntakesLoading(false);
    }
  };

  const refreshAfterIntakeChange = async studentNo => {
    await Promise.all([loadStudentCourses(studentNo), load()]);
    loadStatusCounts();
    api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {});
    const res = await api.get(`/students/${studentNo}`);
    setViewing(res.data.data);
  };

  const handleEnroll = async () => {
    if (!enrollForm.course_code || !enrollForm.intake_code) return;
    setIntakeActionBusy(true);
    try {
      // Status always starts Registered — issuing a receipt for this
      // enrolment is what bumps it to Active — and enrolled_at is today,
      // matching when the enrolment is actually being recorded.
      await api.post(`/students/${viewing.student_no}/courses`, {
        course_code: enrollForm.course_code, intake_code: enrollForm.intake_code,
        enrolled_at: new Date().toISOString().slice(0, 10), status: 'registered',
      });
      toast('Enrolled', 'success');
      setEnrollOpen(false);
      setEnrollForm({ course_code: '', intake_code: '' });
      await refreshAfterIntakeChange(viewing.student_no);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to enrol', 'error');
    } finally {
      setIntakeActionBusy(false);
    }
  };


  const handleEndEnrollment = async (row, status) => {
    setIntakeActionBusy(true);
    try {
      await api.put(`/students/${viewing.student_no}/courses/${row.id}`, { status });
      toast(status === 'completed' ? 'Marked as completed' : 'Marked as withdrawn', 'success');
      await refreshAfterIntakeChange(viewing.student_no);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to update enrolment', 'error');
    } finally {
      setIntakeActionBusy(false);
    }
  };

  const handleMarkActive = async row => {
    setIntakeActionBusy(true);
    try {
      await api.put(`/students/${viewing.student_no}/courses/${row.id}/activate`);
      toast('Marked as active', 'success');
      await refreshAfterIntakeChange(viewing.student_no);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to mark as active', 'error');
    } finally {
      setIntakeActionBusy(false);
    }
  };

  const toggleHistory = async row => {
    if (historyFor === row.id) { setHistoryFor(null); return; }
    setHistoryFor(row.id);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/students/${viewing.student_no}/courses/${row.id}/history`);
      setHistoryRows(res.data.data);
    } catch {
      toast('Failed to load status history', 'error');
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openModules = async row => {
    setModuleTarget(row);
    setModulesLoading(true);
    try {
      const res = await api.get(`/students/${viewing.student_no}/courses/${row.id}/modules`);
      setEnrolmentModules(res.data.data);
    } catch {
      toast('Failed to load modules', 'error');
      setEnrolmentModules([]);
    } finally {
      setModulesLoading(false);
    }
  };

  const handleAddModule = async module_code => {
    setAddingModule(module_code);
    try {
      await api.post(`/students/${viewing.student_no}/courses/${moduleTarget.id}/modules`, { module_code });
      toast('Module added', 'success');
      const res = await api.get(`/students/${viewing.student_no}/courses/${moduleTarget.id}/modules`);
      setEnrolmentModules(res.data.data);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to add module', 'error');
    } finally {
      setAddingModule(null);
    }
  };

  const openTransfer = row => {
    setTransferTarget(row);
    setTransferForm({ course_code: row.course_code || '', to_intake_code: '', transfer_date: new Date().toISOString().slice(0, 10), attendance_migration: 'from_date' });
    setTransferErrors({});
  };

  const handleTransfer = async () => {
    if (!transferForm.to_intake_code) {
      setTransferErrors({ to_intake_code: true });
      toast('Please select a destination intake', 'error');
      return;
    }
    if (!transferForm.transfer_date) {
      setTransferErrors({ transfer_date: true });
      toast('Please select a transfer date', 'error');
      return;
    }
    setTransferErrors({});
    setIntakeActionBusy(true);
    try {
      const res = await api.post(`/students/${viewing.student_no}/courses/${transferTarget.id}/transfer`, transferForm);
      toast('Student transferred', 'success');
      if (res.data.attendanceWarning) toast(res.data.attendanceWarning, 'error');
      setTransferTarget(null);
      await refreshAfterIntakeChange(viewing.student_no);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to transfer', 'error');
    } finally {
      setIntakeActionBusy(false);
    }
  };

  const INTAKE_STATUS_BADGE = {
    registered:  'badge-withdrawn',
    active:      'badge-active',
    completed:   'badge-inactive',
    withdrawn:   'badge-absent',
    deferred:    'badge-deferred',
    transferred: 'badge-late',
  };
  // 'registered' — the enrolment is on record but the student hasn't paid
  // yet, so it isn't Active. Becomes 'active' once a receipt is issued for it.
  const INTAKE_STATUS_LABEL = {
    registered:     'Registered',
    active:      'Active',
    completed:   'Completed',
    withdrawn:   'Withdrawn',
    deferred:    'Deferred',
    transferred: 'Transferred',
  };

  const SECTION_ENDPOINT = {
    attendance: s => `/attendance/student/${s.student_no}`,
    academic:   s => `/academic/summary/${s.student_no}`,
    mcq:        s => `/academic/mcq/${s.student_no}`,
    payments:   s => `/receipts?student_id=${s.student_no}`,
  };

  // Attendance is special-cased: its endpoint takes intake_code/module_code
  // filters the admin can change after the section is already open, so it
  // needs its own re-fetchable loader instead of the fetch-once pattern the
  // other sections use.
  const loadAttendanceRate = async () => {
    if (!viewing) return;
    setSections(p => ({ ...p, attendance: { ...p.attendance, loading: true } }));
    try {
      const params = {};
      if (attendanceIntake) params.intake_code = attendanceIntake;
      if (attendanceModule) params.module_code = attendanceModule;
      const r = await api.get(`/attendance/student/${viewing.student_no}`, { params });
      setSections(p => ({ ...p, attendance: { ...p.attendance, loading: false, data: r.data.data } }));
    } catch {
      setSections(p => ({ ...p, attendance: { ...p.attendance, loading: false, data: null } }));
    }
  };
  useEffect(() => { if (sections.attendance.open) loadAttendanceRate(); }, [attendanceIntake, attendanceModule]);

  // Modules available for the Attendance Rate section's chosen intake.
  useEffect(() => {
    setAttendanceModule('');
    setAttendanceModules([]);
    const intakeObj = intakes.find(i => i.intake_code === attendanceIntake);
    if (!intakeObj) return;
    api.get(`/intakes/${intakeObj.id}/modules`).then(r => setAttendanceModules(r.data.data)).catch(() => {});
  }, [attendanceIntake]);

  const toggleSection = key => {
    setSections(prev => {
      const cur = prev[key];
      const nextOpen = !cur.open;
      // Fetch on first expand only — collapsing/re-expanding reuses the
      // already-loaded data instead of re-fetching.
      if (nextOpen && cur.data === null && !cur.loading) {
        if (key === 'attendance') {
          loadAttendanceRate();
          return { ...prev, [key]: { ...cur, open: nextOpen, loading: true } };
        }
        api.get(SECTION_ENDPOINT[key](viewing))
          .then(r => setSections(p => ({ ...p, [key]: { ...p[key], loading: false, data: r.data.data } })))
          .catch(() => setSections(p => ({ ...p, [key]: { ...p[key], loading: false, data: [] } })));
        return { ...prev, [key]: { ...cur, open: nextOpen, loading: true } };
      }
      return { ...prev, [key]: { ...cur, open: nextOpen } };
    });
  };

  // Headline counts for the stat cards above the filter bar — from the
  // dedicated /students/status-counts endpoint (a single GROUP BY over the
  // whole table), so they always read "of everyone" regardless of which
  // page/filter the table below is currently showing.
  const {
    active: activeCount, registered: registeredCount, withdrawn: withdrawnCount,
    inactive: inactiveCount, deferred: deferredCount, graduated: graduatedCount,
    all_time_total: grandTotal, // unscoped (every year) — the cards above are this-year only
  } = statusCounts;

  // Clicking a stat card toggles that status into the Status filter below
  // (clicking the same one again clears it) instead of opening a separate view.
  const toggleStatusFilter = status => {
    if (filterStatus === status && !statusCleared) { setStatusCleared(true); return; }
    setFilterStatus(status);
    setStatusCleared(false);
  };

  // Filtering, sorting, and paging all happen server-side now (see load()) —
  // `students` here is already just the current page's rows.
  const totalPages = Math.max(1, Math.ceil(serverTotal / pageSize));
  const start = (page - 1) * pageSize;

  const refreshEditingPicture = async () => {
    if (!editing) return;
    const res = await api.get(`/students/${editing.student_no}`);
    setEditing(res.data.data);
  };

  const openCreate = () => {
    setEditing(null); setForm(EMPTY); setFormCourse(''); setGuidingLanguageSelection('');
    setFormError(''); setFieldErrors({}); setShowForm(true);
  };
  const openEdit   = s => {
    setEditing(s);
    setGuidingLanguageSelection(canonicalGuidingLanguage(s.guiding_language || ''));
    setForm({
      name:s.name||'', title:s.title||'', nric:s.nric||'', identity_type:s.identity_type||'',
      gender:s.gender||'', citizenship:s.citizenship||'', nationality:s.nationality||'',
      date_of_birth:s.date_of_birth?.slice(0,10)||'', race:s.race||'',
      email:s.email||'', phone:s.phone||'', contact_no_country_code:s.contact_no_country_code||'',
      intake:s.intake||'', language:s.language||'', guiding_language:canonicalGuidingLanguage(s.guiding_language || ''),
      education_level:s.education_level||'', employment_designation:s.employment_designation||'',
      employment_salary_range:s.employment_salary_range||'', next_of_kin:s.next_of_kin||'',
      next_of_kin_contact:s.next_of_kin_contact||'', type_of_application:s.type_of_application||'',
      enrollment_date:s.enrollment_date?.slice(0,10)||'', status:s.status||'active', remarks:s.remarks||'',
      notify_email: s.notify_email != null ? !!s.notify_email : true, notify_whatsapp: !!s.notify_whatsapp,
    });
    setFormCourse(intakes.find(i => i.intake_code === s.intake)?.course_code || '');
    setFormError(''); setFieldErrors({}); setShowForm(true);
  };

  const validateForm = () => {
    const errs = {};
    if (!form.name.trim())          errs.name            = 'Full name is required.';
    if (!form.identity_type)        errs.identity_type   = 'Identity type is required.';
    // Same identityValueError() the import review uses — NRIC/FIN are
    // checked against their real Singapore formats; Passport only gets a
    // loose plausible-length/charset check since formats vary by country.
    const nricErr = identityValueError(form.identity_type, form.nric);
    if (nricErr) errs.nric = nricErr;
    if (!form.gender)               errs.gender          = 'Gender is required.';
    if (!form.citizenship)          errs.citizenship     = 'Citizenship is required.';
    if (!form.nationality)          errs.nationality     = 'Nationality is required.';
    if (!form.email.trim())         errs.email           = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Enter a valid email address.';
    if (!form.phone.trim())         errs.phone           = 'Phone number is required.';
    else if (!/^\d+$/.test(form.phone.trim())) errs.phone = 'Phone number must contain digits only.';
    // Course/intake are only editable at creation — changing an existing
    // student's intake goes through Enrol/Transfer on the Courses section so
    // student_courses (audit trail, sequence, attendance migration) stays in
    // sync, so there's nothing to validate here once editing.
    if (!editing) {
      if (!formCourse)             errs.course          = 'Course is required.';
      if (!form.intake)            errs.intake          = 'Intake is required.';
    }
    if (!form.language)             errs.language        = 'Language is required.';
    if (!form.guiding_language.trim() || form.guiding_language.trim().toLowerCase() === GUIDING_LANGUAGE_OTHER.toLowerCase()) {
      errs.guiding_language = 'Enter the specific guiding language when Others is selected.';
    }
    if (!form.enrollment_date)      errs.enrollment_date = 'Enrolment date is required.';
    if (!form.status)               errs.status          = 'Status is required.';
    return errs;
  };

  const handleSave = async e => {
    e.preventDefault(); setFormError('');
    const errs = validateForm();
    if (Object.keys(errs).length) { setFieldErrors(errs); setFormError('Some fields need your attention before this can be saved.'); return; }
    setFieldErrors({});
    setSaving(true);
    try {
      if (editing) {
        const res = await api.put(`/students/${editing.student_no}`, form);
        await attachRef.current?.flush(editing.student_no);
        const updatedCourses = Number(res.data.coursesUpdated) || 0;
        if (viewing?.student_no === editing.student_no) {
          // Keep an already-open details drawer in sync with the profile save;
          // otherwise the database changes but the course badge stays stale.
          await Promise.all([
            loadStudentCourses(editing.student_no),
            api.get(`/students/${editing.student_no}`).then(r => setViewing(r.data.data)).catch(() => {}),
          ]);
        }
        toast(['withdrawn', 'deferred'].includes(form.status)
          ? updatedCourses
            ? `Student updated; ${updatedCourses} course${updatedCourses === 1 ? '' : 's'} marked ${form.status === 'deferred' ? 'Deferred' : 'Withdrawn'}`
            : 'Student updated; no active or registered courses needed changing'
          : 'Student updated', 'success');
      } else {
        const res = await api.post('/students', form);
        await attachRef.current?.flush(res.data.student_no);
        await pictureRef.current?.flush(res.data.student_no);
        toast('Student created', 'success');
      }
      const savedGuidingLanguage = canonicalGuidingLanguage(form.guiding_language);
      if (savedGuidingLanguage && !GUIDING_LANGUAGE_OPTIONS.some(value => guidingLanguageKey(value) === guidingLanguageKey(savedGuidingLanguage))) {
        setSavedGuidingLanguages(prev => prev.some(value => guidingLanguageKey(value) === guidingLanguageKey(savedGuidingLanguage))
          ? prev
          : [...prev, savedGuidingLanguage]);
      }
      setShowForm(false); load(); loadStatusCounts();
      if (!editing) api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {});
    }
    catch (err) { setFormError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/students/${confirmDel}`); toast('Student deleted','success'); setConfirmDel(null); setDeleteError(''); load(); loadStatusCounts(); }
    catch (err) { setDeleteError(err.response?.data?.message||'Delete failed'); }
  };

  // Turns one file's raw preview rows into the shape importPreview stores —
  // shared by every file in the batch so multi-file and single-file imports
  // apply the exact same defaulting/matching logic.
  const mapPreviewRows = (rawRows, intakeByCode) => rawRows.map(r => {
    const data = { ...r.data };
    // The file has no Notify via columns — default the same way the Add
    // Student form does (EMPTY), rather than leaving them unset.
    if (data.notify_email === undefined) data.notify_email = true;
    if (data.notify_whatsapp === undefined) data.notify_whatsapp = false;
    const matched = data.intake ? intakeByCode[data.intake] : null;
    // Language and Guiding Language are the same for almost every
    // applicant — if the file only gave one, default the other to match.
    if (data.language && !data.guiding_language) data.guiding_language = data.language;
    else if (data.guiding_language && !data.language) data.language = data.guiding_language;
    const importedGuidingLanguage = canonicalGuidingLanguage(data.guiding_language);
    const guidingLanguageSelection = guidingLanguageOptions.some(value => guidingLanguageKey(value) === guidingLanguageKey(importedGuidingLanguage))
      ? importedGuidingLanguage
      : '';
    data.guiding_language = importedGuidingLanguage === GUIDING_LANGUAGE_OTHER ? '' : importedGuidingLanguage;
    // Starts collapsed — the reviewer opens one card at a time (see
    // togglePreviewRowCollapsed), fills it in, and closes it again to
    // keep the list manageable while working through the rest.
    return { ...r, data, course: matched ? matched.course_code : '', guidingLanguageSelection, collapsed: true };
  });

  const handleImport = async e => {
    const allFiles = Array.from(e.target.files || []);
    if (!allFiles.length) return;
    // Common spreadsheet extensions are accepted (the input's own `accept`
    // already steers the file picker, but that's just a suggestion the OS
    // can ignore) — anything else the user drags/pastes in gets rejected up
    // front rather than sent to a preview endpoint that will just fail on it.
    const isSpreadsheetFile = f => /\.(csv|tsv|xlsx|xls|xlsm|xlsb|xltx|xltm|xlt|ods|fods|dif|sylk|prn)$/i.test(f.name);
    const files = allFiles.filter(isSpreadsheetFile);
    const rejected = allFiles.filter(f => !isSpreadsheetFile(f));
    if (rejected.length) {
      toast(`${rejected.length} file(s) skipped — only spreadsheet files are supported (${rejected.map(f=>f.name).join(', ')})`, 'error');
    }
    if (!files.length) { e.target.value=''; return; }

    setImporting(true);
    setImportProgress({ current: 0, total: files.length, fileName: files[0].name });
    const intakeByCode = Object.fromEntries(intakes.map(i => [i.intake_code, i]));
    const allRows = [];
    const failedFiles = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setImportProgress({ current: i, total: files.length, fileName: file.name });
        const fd = new FormData(); fd.append('file', file);
        try {
          const res = await api.post('/students/import/preview', fd, { headers:{'Content-Type':'multipart/form-data'} });
          // Nothing is saved yet — the preview response just tells us which
          // applicant rows parsed, which are dupes/incomplete (skip), and
          // which of the 11 applicant-only fields came back blank per row.
          // The reviewer fills those in (see importPreview state) before
          // anything is written, via handleConfirmImport below. Course is a
          // client-side filter for the Intake dropdown only (same
          // relationship as formCourse/form.intake in the Add Student form)
          // — if the file's Intake Code already matches a known intake,
          // pre-select its course so the reviewer isn't re-picking what the
          // file already told us.
          allRows.push(...mapPreviewRows(res.data.rows, intakeByCode));
        } catch (err) {
          failedFiles.push({ name: file.name, message: err.response?.data?.message || 'Import failed' });
        }
        setImportProgress({ current: i + 1, total: files.length, fileName: file.name });
      }
      if (failedFiles.length) {
        toast(`${failedFiles.length} file(s) failed: ${failedFiles.map(f=>`${f.name} (${f.message})`).join('; ')}`, 'error');
      }
      if (allRows.length) setImportPreview({ rows: allRows });
    }
    finally { setImporting(false); setImportProgress(null); e.target.value=''; }
  };

  const setPreviewRowData = (idx, key, value) => {
    setImportPreview(p => {
      const rows = p.rows.slice();
      const data = { ...rows[idx].data, [key]: value };
      // Language and Guiding Language are the same thing for almost every
      // applicant — picking Language keeps Guiding Language in sync so the
      // reviewer isn't setting the same value twice (still freely editable
      // afterward for the rare case where they genuinely differ).
      if (key === 'language') data.guiding_language = value;
      rows[idx] = { ...rows[idx], data, ...(key === 'language' ? { guidingLanguageSelection: value } : {}) };
      return { rows };
    });
  };

  const setPreviewRowGuidingLanguageSelection = (idx, value) => {
    setImportPreview(p => {
      const rows = p.rows.slice();
      const data = { ...rows[idx].data, guiding_language: value === GUIDING_LANGUAGE_OTHER ? '' : value };
      rows[idx] = { ...rows[idx], data, guidingLanguageSelection: value };
      return { rows };
    });
  };

  // Unlike the Add Student form's formCourse/intake pair, changing the
  // course here does NOT clear the already-chosen intake (or any other
  // field) — the reviewer's edits stay put until they change them directly.
  const setPreviewRowCourse = (idx, course) => {
    setImportPreview(p => {
      const rows = p.rows.slice();
      rows[idx] = { ...rows[idx], course };
      return { rows };
    });
  };

  const togglePreviewRowCollapsed = (idx) => {
    setImportPreview(p => {
      const rows = p.rows.slice();
      rows[idx] = { ...rows[idx], collapsed: !rows[idx].collapsed };
      return { rows };
    });
  };

  // Unchecking a row (default checked) leaves it in the review list without
  // blocking "Import All" on it — lets the reviewer save the ones that are
  // ready now and come back to the rest later instead of finishing every
  // row in one sitting.
  const setPreviewRowIncluded = (idx, included) => {
    setImportPreview(p => {
      const rows = p.rows.slice();
      rows[idx] = { ...rows[idx], included };
      return { rows };
    });
  };

  const removePreviewRow = (idx) => {
    setImportPreview(p => {
      const rows = p.rows.slice();
      rows.splice(idx, 1);
      return rows.length ? { rows } : null;
    });
  };

  // Shared by the "Import all" gate and each card's Complete/Needs info badge.
  // A select field is only really "filled in" if the value is one of its
  // real options — a raw file value the list doesn't recognize (e.g. a
  // demonym or abbreviation) must count as blank too, same as RF() above.
  const reviewFieldIsBlank = (f, value) => {
    if (f.type === 'select') return !isValidOption(value, f.options.map(o => typeof o === 'string' ? { value:o, label:o } : o));
    return !value;
  };
  const previewRowIncomplete = (r) => !r.skip && (
    !r.data.name
    || !isValidOption(r.data.citizenship, citizenshipOptionsFor(countries))
    || !isValidOption(r.data.nationality, distinctLabels(countries.map(c=>displayTitleCase(c.nationality))))
    || REVIEW_FIELDS.some(f => reviewFieldIsBlank(f, r.data[f.key]))
    || CORE_REVIEW_FIELDS.filter(f => f.key !== 'guiding_language').some(f => reviewFieldIsBlank(f, r.data[f.key]))
    || (r.guidingLanguageSelection === GUIDING_LANGUAGE_OTHER
      ? !String(r.data.guiding_language || '').trim() || guidingLanguageKey(r.data.guiding_language) === GUIDING_LANGUAGE_OTHER.toLowerCase()
      : !isValidOption(r.data.guiding_language, guidingLanguageOptions.map(value => ({ value, label: value }))))
    || !r.course || !r.data.intake || !r.data.status
    || !!identityValueError(r.data.identity_type, r.data.nric)
  );
  // A row counts as "marked" for this pass unless the reviewer explicitly
  // unchecked it — so Import All only has to wait on rows actually going in
  // this round, not ones intentionally left for later.
  const previewRowMarked = r => r.included !== false;
  const previewHasBlanks = importPreview
    ? importPreview.rows.some(r => previewRowMarked(r) && previewRowIncomplete(r))
    : false;
  const markedForImportCount = importPreview
    ? importPreview.rows.filter(r => !r.skip && previewRowMarked(r)).length
    : 0;

  const handleConfirmImport = async () => {
    // Skipped rows always ride along (confirmImportStudents() just tallies
    // them into skipReasons); unmarked ones stay behind in the review list
    // for a later pass instead of being sent this round.
    const toImport = importPreview.rows.filter(r => r.skip || previewRowMarked(r));
    const remaining = importPreview.rows.filter(r => !r.skip && !previewRowMarked(r));
    setImportConfirming(true);
    try {
      const res = await api.post('/students/import/confirm', { rows: toImport });
      // Leftover unmarked rows keep the review modal open so the reviewer
      // can pick up where they left off after seeing this batch's results.
      setImportPreview(remaining.length ? { rows: remaining } : null);
      setImportResult(res.data);
      const importedGuidingLanguages = toImport
        .filter(row => !row.skip)
        .map(row => canonicalGuidingLanguage(row.data?.guiding_language))
        .filter(value => value && value.toLowerCase() !== GUIDING_LANGUAGE_OTHER.toLowerCase()
          && !GUIDING_LANGUAGE_OPTIONS.some(option => guidingLanguageKey(option) === guidingLanguageKey(value)));
      if (importedGuidingLanguages.length) {
        setSavedGuidingLanguages(prev => Array.from(new Map(
          [...prev, ...importedGuidingLanguages].map(value => [guidingLanguageKey(value), canonicalGuidingLanguage(value)])
        ).values()));
      }
      load(); loadStatusCounts();
      api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {});
    }
    catch (err) { toast(err.response?.data?.message||'Import failed','error'); }
    finally { setImportConfirming(false); }
  };

  // Hands the capacity-exceeded / intake-mismatch rows from the just-shown
  // results modal off to the existing resolver modals — same shape as
  // before, just triggered from a "Resolve" button instead of auto-opening.
  const openCapacityFromResult = () => {
    setCapacityLeftover(importResult.capacityExceeded.map(s => ({
      key: s.student_no, name: s.name, detail: `${s.email} · intake '${s.attempted_intake_code}' was full`, student_no: s.student_no,
    })));
    setImportResult(null);
    setShowCapacityModal(true);
  };
  const openMismatchFromResult = () => {
    setMismatchLeftover(importResult.intakeMismatches.map(s => ({
      key: s.student_no, name: s.name, detail: `${s.email} · intake '${s.attempted_intake_code}' doesn't match any existing intake`, student_no: s.student_no,
    })));
    setImportResult(null);
    setShowMismatchModal(true);
  };

  const assignLeftoverIntake = async (row, course_code, intake_code) => {
    await api.post(`/students/${row.student_no}/courses`, {
      intake_code, status: 'registered', enrolled_at: new Date().toISOString().slice(0, 10),
    });
    load();
    api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {});
  };

  const handleExport = async () => { const res = await api.get('/students/export',{responseType:'blob'}); const a=document.createElement('a'); a.href=URL.createObjectURL(res.data); a.download='students.xlsx'; a.click(); };
  const handleTemplate = async () => { const res = await api.get('/students/template',{responseType:'blob'}); const a=document.createElement('a'); a.href=URL.createObjectURL(res.data); a.download='student_import_template.xlsx'; a.click(); };

  const fe = fieldErrors;
  const setField = (field, val) => { setForm(f=>({...f,[field]:val})); setFieldErrors(e=>({...e,[field]:''})); };

  const F = (field, label, type='text', opts={}) => (
    <div className={opts.full?'col-12':'col-12 col-sm-6'}>
      <label htmlFor={`sf-${field}`} style={{fontSize:12,fontWeight:500,color:fe[field]?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>{label} *</label>
      {opts.select ? (
        <SearchableSelect id={`sf-${field}`} value={opts.value !== undefined ? opts.value : form[field]||''} onChange={opts.onChange || (e=>setField(field,e.target.value))} options={opts.options}
          style={fe[field]?{borderColor:'#b91c1c'}:{}} />
      ) : (
        <div style={{ position: 'relative' }}>
          <input id={`sf-${field}`} type={type} className="sms-form-control" value={form[field]||''}
            onChange={e=>{
              let v = e.target.value;
              if (type==='tel') v = v.replace(/\D/g,'');
              else if (opts.format) v = opts.format(v);
              setField(field, v);
            }}
            style={{ ...(fe[field]?{borderColor:'#b91c1c'}:{}), ...(opts.copyValue ? { paddingRight: 38 } : {}) }}
            inputMode={type==='tel'?'numeric':undefined} />
          {opts.copyValue && (
            <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
              <CopyPhoneButton onCopy={() => copyPhone(opts.copyValue)} />
            </span>
          )}
        </div>
      )}
      {fe[field] && <div style={{fontSize: 12,color: 'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{fe[field]}</div>}
    </div>
  );

  const ST = (label, field) => <SortTh label={label} field={field} sortKey={sort.key} sortDir={sort.dir} onSort={toggleSort} />;

  // Collapsible detail-modal section — hidden by default, content stays
  // hidden until the admin clicks the header to expand it (and fetches on
  // that first click only).
  // icon/color pairs match the Dashboard's StatCard treatment — icon in
  // full color on a tinted (12% alpha) circle of the same color, which
  // self-contrasts well over both light and dark --sms-sur.
  const SECTION_STYLE = {
    attendance: { icon: 'bi-calendar-check', color: 'var(--sms-stat-green)' },
    academic:   { icon: 'bi-graph-up',       color: 'var(--sms-stat-violet)' },
    mcq:        { icon: 'bi-list-check',     color: 'var(--sms-stat-cyan)' },
    payments:   { icon: 'bi-receipt',        color: 'var(--sms-stat-amber)' },
  };

  const Section = (key, label, renderContent) => {
    const sec = sections[key];
    const { icon, color } = SECTION_STYLE[key];
    return (
      <div className="mt-3">
        <button type="button" onClick={()=>toggleSection(key)}
          className="sms-btn sms-btn-outline w-100 d-flex justify-content-between align-items-center" style={{fontSize:12}}>
          <span className="d-flex align-items-center gap-2">
            <span style={{ width: 24, height: 24, borderRadius: 7, background: color+'1f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={`bi ${icon}`} style={{ color, fontSize: 13 }} />
            </span>
            {label}
          </span>
          <i className={`bi ${sec.open?'bi-chevron-up':'bi-chevron-down'}`} />
        </button>
        {sec.open && (
          <div className="mt-2">
            {sec.loading ? <Spinner minHeight="200px"/> : renderContent(sec.data)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <PageHeader title="Student Profile" subtitle={`${serverTotal} of ${grandTotal} students`}
        actions={
          <div className="sms-page-actions">
            {can('students','view') && <>
              <button className="sms-btn sms-btn-info" onClick={handleTemplate} style={{fontSize:12}}><i className="bi bi-download"/> Template</button>
              <button className="sms-btn sms-btn-success" onClick={handleExport} style={{fontSize:12}}><i className="bi bi-file-earmark-excel"/> Export</button>
            </>}
            {can('students','create') && <>
              <button className="sms-btn sms-btn-warning" onClick={()=>fileRef.current.click()} disabled={importing} style={{fontSize:12}}>
                {importing?<InlineSpinner/>:<i className="bi bi-upload"/>} Import
              </button>
              <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg"/> Add Student</button>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.xlsx,.xls,.xlsm,.xlsb,.xltx,.xltm,.xlt,.ods,.fods,.dif,.sylk,.prn" multiple style={{display:'none'}} onChange={handleImport}/>
            </>}
          </div>
        }
      />

      {importProgress && (
        <div className="sms-card mb-3" style={{padding:'10px 14px'}}>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'var(--sms-txt2)',marginBottom:6}}>
            <span>Importing file {Math.min(importProgress.current+1, importProgress.total)} of {importProgress.total} — {importProgress.fileName}</span>
            <span>{fmtPct(Math.round((importProgress.current/importProgress.total)*100))}</span>
          </div>
          <div style={{height:6,borderRadius:3,background:'var(--sms-bdr)',overflow:'hidden'}}>
            <div style={{height:'100%',borderRadius:3,background:'var(--sms-acc)',width:`${(importProgress.current/importProgress.total)*100}%`,transition:'width .25s ease'}}/>
          </div>
        </div>
      )}

      {/* Headline counts — click one to toggle it as the Status filter. Cards on
          tablet/desktop; a Status dropdown in the filter bar below takes over
          on mobile instead, where six cards would just wrap into a stack. */}
      <div className="row g-3 mb-3 sms-stat-anim-row sms-status-cards">
        <div className="col-6 col-sm-4 col-lg-2">
          <ProfileStatCard label="Active" value={activeCount} icon="bi-person-check-fill" color="var(--sms-stat-green)"
            active={filterStatus === 'active' && !statusCleared} onClick={() => toggleStatusFilter('active')} />
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <ProfileStatCard label="Registered" value={registeredCount} icon="bi-person-plus-fill" color="var(--sms-stat-amber)"
            active={filterStatus === 'registered' && !statusCleared} onClick={() => toggleStatusFilter('registered')} />
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <ProfileStatCard label="Withdrawn" value={withdrawnCount} icon="bi-person-x-fill" color="var(--sms-stat-red)"
            active={filterStatus === 'withdrawn' && !statusCleared} onClick={() => toggleStatusFilter('withdrawn')} />
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <ProfileStatCard label="Inactive" value={inactiveCount} icon="bi-person-dash-fill" color="var(--sms-stat-blue)"
            active={filterStatus === 'inactive' && !statusCleared} onClick={() => toggleStatusFilter('inactive')} />
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <ProfileStatCard label="Deferred" value={deferredCount} icon="bi-pause-circle-fill" color="var(--sms-stat-cyan)"
            active={filterStatus === 'deferred' && !statusCleared} onClick={() => toggleStatusFilter('deferred')} />
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <ProfileStatCard label="Graduated" value={graduatedCount} icon="bi-mortarboard-fill" color="var(--sms-stat-violet)"
            active={filterStatus === 'graduated' && !statusCleared} onClick={() => toggleStatusFilter('graduated')} />
        </div>
      </div>

      {/* Table filters */}
      <div className="sms-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-lg-4">
            <label style={{fontSize: 12,color:'var(--sms-txt2)',display:'block',marginBottom:4}}>Search</label>
            <div style={{position:'relative'}}>
              <i className="bi bi-search" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'var(--sms-txt3)',fontSize:13}}/>
              <input className="sms-form-control" style={{paddingLeft:28}} placeholder="Student ID, name, NRIC, email, phone…" value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
          </div>
          {/* Mobile-only — the six status cards above take over on tablet/desktop */}
          <div className="col-12 sms-status-dropdown-wrap">
            <label style={{fontSize: 12,color:'var(--sms-txt2)',display:'block',marginBottom:4}}>Status</label>
            <SearchableSelect value={filterStatus} onChange={e=>{setFilterStatus(e.target.value);setStatusCleared(false);}} placeholder="All"
              options={['registered','active','inactive','withdrawn','deferred','graduated'].map(s=>({value:s,label:s.charAt(0).toUpperCase()+s.slice(1)}))} />
          </div>
          <div className="col-6 col-lg-2">
            <label style={{fontSize: 12,color:'var(--sms-txt2)',display:'block',marginBottom:4}}>Year</label>
            <SearchableSelect value={String(filterYear)} onChange={e=>setFilterYear(Number(e.target.value))}
              options={ENROLMENT_YEARS.map(y=>({value:String(y),label:String(y)}))} />
          </div>
          <div className="col-6 col-lg-4">
            <label style={{fontSize: 12,color:'var(--sms-txt2)',display:'block',marginBottom:4}}>
              Intake{filterIntakes.length > 0 && <span style={{marginLeft:5,background:'var(--sms-btn-primary-bg)',color:'#fff',borderRadius:10,fontSize: 12,padding:'1px 6px'}}>{filterIntakes.length}</span>}
            </label>
            <div className="d-flex gap-2">
              <div style={{flex:1,minWidth:0}}><IntakeMultiSelect intakes={intakes} selected={filterIntakes} onChange={setFilterIntakes}/></div>
              <button className="sms-btn sms-btn-outline-danger" style={{minHeight:42,width:42,padding:0,justifyContent:'center',flexShrink:0}}
                onClick={()=>{setSearch('');setFilterIntakes([]);setFilterStatus('');setStatusCleared(false);setFilterNeedsModules(false);}}
                title="Clear all filters">
                <i className="bi bi-x-circle"/>
              </button>
            </div>
          </div>
          <div className="col-12">
            <button type="button" onClick={()=>setFilterNeedsModules(v=>!v)}
              className={`badge rounded-pill ${filterNeedsModules ? 'badge-late' : ''}`}
              style={{border:'1px solid', borderColor: filterNeedsModules ? 'transparent' : 'var(--sms-bdr)',
                background: filterNeedsModules ? undefined : 'transparent',
                color: filterNeedsModules ? undefined : 'var(--sms-txt2)',
                fontSize: 12,fontWeight:500,padding:'6px 12px',display:'inline-flex',alignItems:'center',gap:6,cursor:'pointer'}}
              title="Show only students whose course has no modules set up yet">
              <span style={{width:6,height:6,borderRadius:'50%',background:'var(--sms-stat-amber)',display:'inline-block',flexShrink:0}} />
              Needs Modules
              {filterNeedsModules && <i className="bi bi-x-lg" style={{fontSize: 12,marginLeft:2}}/>}
            </button>
          </div>
        </div>
      </div>

      <div className="sms-split-row">
      <div className="sms-card sms-split-table" style={{padding:0,overflow:'hidden'}}>
        {!loading && grandTotal===0 ? <EmptyState icon="bi-people" message="No students found"/> : (
          <>
            <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:6,padding:'8px 12px',
                background:'color-mix(in srgb, var(--sms-stat-amber) 8%, transparent)',borderBottom:'1px solid var(--sms-bdr)'}}>
              <span style={{width:7,height:7,borderRadius:'50%',background:'var(--sms-stat-amber)',display:'inline-block',flexShrink:0}} />
              <span style={{fontSize: 12,fontWeight:600,color:'var(--sms-stat-amber)'}}>Needs Modules</span>
              <span style={{fontSize: 12,color:'var(--sms-txt2)'}}>— student's course is active/registered but has no modules assigned yet</span>
            </div>
            <div style={{overflowX:'auto'}}>
              <table className="sms-table">
                <thead>
                  <tr>
                    <th style={{width:40}}>#</th>
                    {ST('Student ID','student_no')}{ST('Name','name')}{ST('NRIC','nric')}{ST('Intake','intake')}{ST('Email','email')}{ST('Phone','phone')}{ST('Enrol Date','enrollment_date')}{ST('Status','status')}
                    {(can('students','update')||can('students','delete')) && <th style={{width:90}}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableSkeletonRows columns={9 + ((can('students','update')||can('students','delete')) ? 1 : 0)} />
                  ) : serverTotal===0 ? (
                    <tr><td colSpan={10} style={{textAlign:'center',padding:24,color:'var(--sms-txt3)'}}>No records match your filters</td></tr>
                  ) : students.map((s,idx)=>(
                    <tr key={s.student_no} onClick={()=>{setViewing(s);setViewingOpen(true);}} style={{cursor:'pointer'}}
                      className={viewingOpen && viewing?.student_no===s.student_no?'sms-row-active':''}>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          {s.needs_modules && (
                            <span title="Enrolled course has no modules set up yet"
                              style={{width:7,height:7,borderRadius:'50%',background:'var(--sms-stat-amber)',display:'inline-block',flexShrink:0}} />
                          )}
                          {start+idx+1}
                        </div>
                      </td>
                      <td>{s.student_no||'—'}</td>
                      <td style={{ fontWeight:500 }}>{s.name}</td>
                      <td>{s.nric||'—'}</td>
                      <td>{s.intake||'—'}</td>
                      <td>{s.email||'—'}</td>
                      <td>{s.phone||'—'}</td>
                      <td>{fmtDate(s.enrollment_date)}</td>
                      <td><Badge value={s.status} map={statusBadgeMap}/></td>
                      {(can('students','update')||can('students','delete')) && (
                        <td onClick={e=>e.stopPropagation()}><div className="d-flex gap-1">
                          {can('students','update') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px'}} onClick={()=>openEdit(s)} aria-label="Edit"><i className="bi bi-pencil"/></button>}
                          {can('students','delete') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px',color: 'var(--sms-btn-outline-danger-txt)'}} onClick={()=>{setDeleteError('');setConfirmDel(s.student_no);}} aria-label="Delete"><i className="bi bi-trash"/></button>}
                        </div></td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && <Pagination page={page} totalPages={totalPages} total={serverTotal} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start}/>}
          </>
        )}
      </div>

      <DetailPanel show={showForm} onClose={()=>setShowForm(false)} title={editing?'Edit Student':'Add Student'}>
        <form onSubmit={handleSave} noValidate>
          {formError && <div className="alert alert-danger py-2 mb-3" style={{fontSize:13}}>{formError}</div>}
          <div className="row g-3">
            {editing && (
              <div className="col-12 col-sm-6">
                <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Student ID</label>
                <input className="sms-form-control" value={editing.student_no||'—'} disabled style={{}} />
              </div>
            )}
            <div className={editing ? 'col-12 col-sm-6' : 'col-12'}>
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Profile Picture</label>
              {editing ? (
                <ProfilePicture
                  editable hasPicture={!!editing.profile_picture}
                  getUrl={`/students/${editing.student_no}/picture`}
                  uploadUrl={`/students/${editing.student_no}/picture`}
                  deleteUrl={`/students/${editing.student_no}/picture`}
                  onChange={refreshEditingPicture}
                />
              ) : (
                <ProfilePicture ref={pictureRef} editable uploadUrlFor={id => `/students/${id}/picture`} />
              )}
            </div>
            {F('title','Title','text',{select:true,options:TITLE_OPTIONS.map(v=>({value:v,label:v}))})}
            {F('name','Full Name','text',{format:liveTitleCase})}
            {F('identity_type','Identity Type','text',{select:true,options:IDENTITY_TYPE_OPTIONS.map(v=>({value:v,label:v}))})}
            {(() => {
              // Live feedback as soon as there's something to check against
              // the selected Identity Type — doesn't wait for a submit
              // attempt like the other fields, since a wrong-format NRIC/FIN
              // is a mistake worth catching the moment it's typed. Before a
              // submit attempt, a still-empty field stays quiet (no
              // "Required" nag on a form the user just opened); after one,
              // fe.nric (set by validateForm) takes over so it still shows.
              const liveErr = form.nric ? identityValueError(form.identity_type, form.nric) : '';
              const nricErr = fe.nric || liveErr;
              return (
                <div className="col-12 col-sm-6">
                  <label htmlFor="sf-nric" style={{fontSize:12,fontWeight:500,color:nricErr?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>Identity Value *</label>
                  <input id="sf-nric" className="sms-form-control" value={form.nric||''}
                    onChange={e=>setField('nric',e.target.value.toUpperCase())}
                    placeholder="e.g. S1234567A"
                    style={{...(nricErr?{borderColor:'#b91c1c'}:{}),textTransform:'uppercase'}} />
                  {nricErr && <div style={{fontSize: 12,color: 'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{nricErr}</div>}
                </div>
              );
            })()}
            {F('gender','Gender','text',{select:true,options:[{value:'Male',label:'Male'},{value:'Female',label:'Female'}]})}
            {F('date_of_birth','Date of Birth','date')}
            <div className="col-12 col-sm-6">
              <label htmlFor="sf-citizenship" style={{fontSize:12,fontWeight:500,color:fe.citizenship?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>Citizenship *</label>
              <SearchableSelect id="sf-citizenship" value={form.citizenship||''} onChange={e=>setField('citizenship',e.target.value)} placeholder="-- Select citizenship --"
                options={citizenshipOptionsFor(countries)} style={fe.citizenship?{borderColor:'#b91c1c'}:{}} />
              {fe.citizenship && <div style={{fontSize: 12,color: 'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{fe.citizenship}</div>}
            </div>
            <div className="col-12 col-sm-6">
              <label htmlFor="sf-nationality" style={{fontSize:12,fontWeight:500,color:fe.nationality?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>Nationality *</label>
              <SearchableSelect id="sf-nationality" value={form.nationality||''} onChange={e=>setField('nationality',e.target.value)} placeholder="-- Select nationality --"
                options={distinctLabels(countries.map(c=>displayTitleCase(c.nationality)))} style={fe.nationality?{borderColor:'#b91c1c'}:{}} />
              {fe.nationality && <div style={{fontSize: 12,color: 'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{fe.nationality}</div>}
            </div>
            {F('race','Race','text')}
            {F('email','Email','email',{format:liveLowerCase})}
            {F('contact_no_country_code','Contact No. Country Code','text',{select:true,options:COUNTRY_CALLING_CODES})}
            {F('phone','Phone','tel',{copyValue:form.phone})}
            {editing ? (
              <>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Course</label>
                  <div className="sms-form-control" style={{background:'var(--sms-sur2)',color:'var(--sms-txt2)'}}>
                    {courses.find(c=>c.course_code===formCourse)?.course_name || '—'}
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Intake</label>
                  <div className="sms-form-control" style={{background:'var(--sms-sur2)',color:'var(--sms-txt2)'}}>
                    {form.intake || '—'}
                  </div>
                  <div style={{fontSize: 12,color:'var(--sms-txt3)',marginTop:4}}>
                    To change intake, {' '}
                    <button type="button" onClick={()=>{ setShowForm(false); setViewing(editing); setViewingOpen(true); }}
                      style={{background:'none',border:'none',padding:0,fontSize: 12,color:'var(--sms-acc)',textDecoration:'underline',cursor:'pointer'}}>
                      click here to open the student's details
                    </button>,{' '}then use Enrol / Transfer on the Enrolments section.
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:fe.course?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>Course *</label>
                  <SearchableSelect value={formCourse} onChange={e=>{ setFormCourse(e.target.value); setField('intake',''); setFieldErrors(er=>({...er,course:''})); }} placeholder="-- Select course --"
                    options={courses.filter(c=>c.status==='Active').map(c=>({value:c.course_code,label:c.course_name}))} style={fe.course?{borderColor:'#b91c1c'}:{}} />
                  {fe.course && <div style={{fontSize: 12,color: 'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{fe.course}</div>}
                </div>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:fe.intake?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>Intake *</label>
                  <SearchableSelect value={form.intake} onChange={e=>setField('intake',e.target.value)}
                    placeholder={formCourse ? '-- Select intake --' : '-- Select course first --'} disabled={!formCourse}
                    options={intakeOptionsFor(intakes, formCourse, { dateRange })} style={fe.intake?{borderColor:'#b91c1c'}:{}} />
                  {fe.intake && <div style={{fontSize: 12,color: 'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{fe.intake}</div>}
                </div>
              </>
            )}
            {F('language','Language','text',{select:true,options:[{value:'English',label:'English'},{value:'Chinese',label:'Chinese'}]})}
            {F('guiding_language','Guiding Language','text',{
              select:true,
              value:guidingLanguageSelection,
              options:guidingLanguageOptions.map(v=>({value:v,label:v})),
              onChange:e=>{
                const value = e.target.value;
                setGuidingLanguageSelection(value);
                setField('guiding_language', value === GUIDING_LANGUAGE_OTHER ? '' : value);
              },
            })}
            {guidingLanguageSelection === GUIDING_LANGUAGE_OTHER && (
              <div className="col-12 col-sm-6">
                <label htmlFor="sf-guiding-language-other" style={{fontSize:12,fontWeight:500,color:fe.guiding_language?'#b91c1c':'var(--sms-txt2)',display:'block',marginBottom:5}}>Enter Guiding Language *</label>
                <input id="sf-guiding-language-other" type="text" className="sms-form-control" value={form.guiding_language||''}
                  onChange={e=>setField('guiding_language', e.target.value)} placeholder="e.g. Arabic"
                  required={guidingLanguageSelection === GUIDING_LANGUAGE_OTHER}
                  style={fe.guiding_language?{borderColor:'#b91c1c'}:{}} />
              </div>
            )}
            {F('education_level','Education Level','text')}
            {F('employment_designation','Employment Designation','text')}
            {F('employment_salary_range','Employment Salary Range','text')}
            {F('type_of_application','Type Of Application','text',{select:true,options:TYPE_OF_APPLICATION_OPTIONS.map(v=>({value:v,label:v}))})}
            {F('next_of_kin','Next-of-Kin','text')}
            {F('next_of_kin_contact','Next-of-Kin Contact','tel')}
            {F('enrollment_date','Enrolment Date','date')}
            {editing && F('status','Status','text',{select:true,options:[{value:'registered',label:'Registered',disabled:true},{value:'active',label:'Active'},{value:'inactive',label:'Inactive'},{value:'withdrawn',label:'Withdrawn'},{value:'deferred',label:'Deferred'},{value:'graduated',label:'Graduated'}]})}
            <div className="col-12">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Notify via</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input type="checkbox" className="form-check-input" id="sf-notify-email"
                    checked={!!form.notify_email} onChange={e=>setField('notify_email',e.target.checked)} />
                  <label className="form-check-label" htmlFor="sf-notify-email" style={{fontSize:13}}>Email</label>
                </div>
                <div className="form-check">
                  <input type="checkbox" className="form-check-input" id="sf-notify-whatsapp"
                    checked={!!form.notify_whatsapp} onChange={e=>setField('notify_whatsapp',e.target.checked)} />
                  <label className="form-check-label" htmlFor="sf-notify-whatsapp" style={{fontSize:13}}>WhatsApp</label>
                </div>
              </div>
            </div>
            <div className="col-12">
              <label htmlFor="sf-remarks" style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Remarks</label>
              <textarea id="sf-remarks" className="sms-form-control" rows={3} style={{resize:'vertical'}}
                value={form.remarks||''} onChange={e=>setField('remarks',e.target.value)} />
            </div>
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="students" recordId={editing?.student_no ?? null}
              canUpload={can('students','create')||can('students','update')} canDelete={can('students','delete')} />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="sms-btn sms-btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving?<><InlineSpinner style={{ marginRight: 4 }}/>Saving…</>:(editing?'Update':'Create')}
            </button>
          </div>
        </form>
      </DetailPanel>
      <ConfirmDialog show={!!confirmDel} danger message="Delete this student? This cannot be undone." error={deleteError} onConfirm={handleDelete} onCancel={()=>{setConfirmDel(null);setDeleteError('');}}/>
      <Modal show={!!importPreview} onClose={()=>setImportPreview(null)} title="Review Import" size="lg">
        {importPreview && (() => {
          const skippedCount = importPreview.rows.filter(r => r.skip).length;

          // Simple label+input, bound to one applicant row's data — mirrors
          // the F() helper used by the Add/Edit Student form above, just
          // reading/writing importPreview state instead of `form`.
          const RF = (row, idx, key, label, type='text', opts={}) => {
            const value = row.data[key] || '';
            // For selects, a raw file value that doesn't match any real
            // option (e.g. an abbreviation the list doesn't have) must still
            // count as blank — otherwise it shows the placeholder yet
            // silently passes as "filled in".
            const normOptions = opts.select ? opts.options.map(o => typeof o === 'string' ? { value:o, label:o } : o) : null;
            const cls = opts.select ? (!isValidOption(value, normOptions) ? 'sms-import-blank' : '') : (!value ? 'sms-import-blank' : '');
            return (
              <div className="col-12 col-sm-6">
                <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                  <RFLabel icon={REVIEW_FIELD_ICONS[key]} text={label} />
                </label>
                {opts.select ? (
                  <SearchableSelect value={value} onChange={e=>setPreviewRowData(idx, key, e.target.value)}
                    placeholder={`-- Select ${label.toLowerCase()} --`}
                    options={normOptions}
                    className={cls || undefined} />
                ) : (
                  <input type={type} className={`sms-form-control${cls ? ' '+cls : ''}`} value={value}
                    onChange={e=>setPreviewRowData(idx, key, opts.format ? opts.format(e.target.value) : e.target.value)} />
                )}
              </div>
            );
          };

          const renderGuidingLanguage = (row, idx) => {
            const options = guidingLanguageOptions.map(value => ({ value, label: value }));
            const selection = row.guidingLanguageSelection || '';
            const isOther = selection === GUIDING_LANGUAGE_OTHER;
            const customValue = row.data.guiding_language || '';
            const incomplete = isOther ? !customValue.trim() : !isValidOption(selection, options);
            return (
              <>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                    <RFLabel icon={REVIEW_FIELD_ICONS.guiding_language} text="Guiding Language" />
                  </label>
                  <SearchableSelect value={selection}
                    onChange={e=>setPreviewRowGuidingLanguageSelection(idx, e.target.value)}
                    placeholder="-- Select guiding language --"
                    options={options}
                    className={incomplete ? 'sms-import-blank' : undefined} />
                </div>
                {isOther && (
                  <div className="col-12 col-sm-6">
                    <label htmlFor={`pv-guiding-language-other-${idx}`} style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                      <RFLabel icon={REVIEW_FIELD_ICONS.guiding_language} text="Enter Guiding Language" />
                    </label>
                    <input id={`pv-guiding-language-other-${idx}`} type="text"
                      className={`sms-form-control${incomplete ? ' sms-import-blank' : ''}`}
                      value={customValue}
                      onChange={e=>setPreviewRowData(idx, 'guiding_language', e.target.value)}
                      required={isOther}
                      placeholder="e.g. Arabic" />
                  </div>
                )}
              </>
            );
          };

          return (
            <div className="sms-import-review">
              <div style={{fontSize:12.5,color:'var(--sms-txt2)',marginBottom:10}}>
                {importPreview.rows.length} applicant(s) parsed{skippedCount ? `, ${skippedCount} will be skipped` : ''},
                {' '}{markedForImportCount} marked for this round —
                fill in every highlighted field for a marked applicant before importing. Uncheck one to save it for
                later, or remove it entirely with the trash icon. Click a card's header to collapse it once you're done.
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                {importPreview.rows.map((row, idx) => {
                  const incomplete = previewRowIncomplete(row);
                  const marked = previewRowMarked(row);
                  return (
                  <div key={idx} className="sms-card" style={{padding:0,opacity:row.skip||!marked?0.65:1}}>
                    <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap sms-import-row-header"
                      style={{padding:'12px 16px',cursor:row.skip?'default':'pointer',userSelect:'none'}}
                      onClick={row.skip ? undefined : ()=>togglePreviewRowCollapsed(idx)}>
                      <div className="d-flex align-items-center gap-2 sms-import-row-title" style={{minWidth:0}}>
                        {!row.skip && (
                          <input type="checkbox" className="form-check-input" style={{flexShrink:0,margin:0}}
                            checked={marked} title={marked ? 'Marked for this import round' : 'Not marked — will stay in the list'}
                            onClick={e=>e.stopPropagation()} onChange={e=>setPreviewRowIncluded(idx, e.target.checked)} />
                        )}
                        {!row.skip && (
                          <i className={`bi bi-chevron-${row.collapsed ? 'right' : 'down'}`}
                            style={{fontSize:12,color:'var(--sms-txt3)',flexShrink:0}} aria-hidden="true" />
                        )}
                        <i className="bi bi-person-circle" style={{fontSize:16,color:'var(--sms-txt3)',flexShrink:0}} aria-hidden="true" />
                        <div style={{fontWeight:700,fontSize:13.5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {row.data.name || `Row ${row.rowIndex}`}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 sms-import-row-actions">
                        {row.skip ? (
                          <span className="badge rounded-pill badge-void sms-import-row-status" style={{fontSize: 12}}>
                            <i className="bi bi-x-circle-fill" style={{marginRight:4}} aria-hidden="true"/>Skipped — {row.skipReason}
                          </span>
                        ) : !marked ? (
                          <span className="badge rounded-pill" style={{fontSize: 12,background:'var(--sms-sur2)',color:'var(--sms-txt2)'}}>
                            <i className="bi bi-clock-history" style={{marginRight:4}} aria-hidden="true"/>Saved for later
                          </span>
                        ) : incomplete ? (
                          <span className="badge rounded-pill badge-late" style={{fontSize: 12}}>
                            <i className="bi bi-exclamation-triangle-fill" style={{marginRight:4}} aria-hidden="true"/>Needs info
                          </span>
                        ) : (
                          <span className="badge rounded-pill badge-paid" style={{fontSize: 12}}>
                            <i className="bi bi-check-circle-fill" style={{marginRight:4}} aria-hidden="true"/>Complete
                          </span>
                        )}
                        <button type="button" className="sms-btn sms-btn-outline sms-import-row-remove" title="Remove this applicant from the import"
                          style={{padding:'2px 7px',fontSize:12}}
                          onClick={e=>{ e.stopPropagation(); removePreviewRow(idx); }}>
                          <i className="bi bi-trash" aria-hidden="true"/>
                        </button>
                      </div>
                    </div>
                    {!row.skip && (
                      <div className={`sms-panel-acc${!row.collapsed ? ' expanded' : ''}`}>
                      <div className="sms-panel-acc-inner">
                      <div className="row g-2" style={{padding:'0 16px 16px'}}>
                        {RF(row, idx, 'title', 'Title', 'text', { select:true, options: TITLE_OPTIONS })}
                        {RF(row, idx, 'name', 'Full Name', 'text', { format: liveTitleCase })}
                        {RF(row, idx, 'identity_type', 'Identity Type', 'text', { select:true, options: IDENTITY_TYPE_OPTIONS })}
                        {(() => {
                          const err = identityValueError(row.data.identity_type, row.data.nric);
                          // Empty reads as the same amber "blank" state as every
                          // other required field; a non-blank but wrong-format
                          // value gets the stronger red "invalid" treatment.
                          const cls = !row.data.nric ? 'sms-import-blank' : (err ? 'sms-import-invalid' : '');
                          return (
                            <div className="col-12 col-sm-6">
                              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                                <RFLabel icon={REVIEW_FIELD_ICONS.nric} text="Identity Value" />
                              </label>
                              <input type="text" className={`sms-form-control${cls ? ' '+cls : ''}`}
                                value={row.data.nric||''}
                                onChange={e=>setPreviewRowData(idx, 'nric', e.target.value.toUpperCase())}
                                title={err || undefined}
                                style={{textTransform:'uppercase'}} />
                              {err && <div style={{fontSize: 12,color:'var(--sms-btn-outline-danger-txt)',marginTop:3}}>{err}</div>}
                            </div>
                          );
                        })()}
                        {RF(row, idx, 'gender', 'Gender', 'text', { select:true, options: IMPORT_GENDER_OPTIONS })}
                        {RF(row, idx, 'date_of_birth', 'Date of Birth', 'date')}
                        <div className="col-12 col-sm-6">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon={REVIEW_FIELD_ICONS.citizenship} text="Citizenship" />
                          </label>
                          <SearchableSelect value={row.data.citizenship||''} onChange={e=>setPreviewRowData(idx,'citizenship',e.target.value)}
                            placeholder="-- Select citizenship --"
                            options={citizenshipOptionsFor(countries)}
                            className={!isValidOption(row.data.citizenship, citizenshipOptionsFor(countries)) ? 'sms-import-blank' : undefined} />
                        </div>
                        <div className="col-12 col-sm-6">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon={REVIEW_FIELD_ICONS.nationality} text="Nationality" />
                          </label>
                          <SearchableSelect value={row.data.nationality||''} onChange={e=>setPreviewRowData(idx,'nationality',e.target.value)}
                            placeholder="-- Select nationality --"
                            options={distinctLabels(countries.map(c=>displayTitleCase(c.nationality)))}
                            className={!isValidOption(row.data.nationality, distinctLabels(countries.map(c=>displayTitleCase(c.nationality)))) ? 'sms-import-blank' : undefined} />
                        </div>
                        {RF(row, idx, 'race', 'Race', 'text')}
                        {RF(row, idx, 'email', 'Email', 'email', { format: liveLowerCase })}
                        {RF(row, idx, 'contact_no_country_code', 'Contact No. Country Code', 'text', { select:true, options: COUNTRY_CALLING_CODES })}
                        {RF(row, idx, 'phone', 'Phone', 'tel')}
                        <div className="col-12 col-sm-6">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon={REVIEW_FIELD_ICONS.course} text="Course" />
                          </label>
                          <SearchableSelect value={row.course} onChange={e=>setPreviewRowCourse(idx, e.target.value)}
                            placeholder="-- Select course --"
                            options={courses.filter(c=>c.status==='Active').map(c=>({value:c.course_code,label:c.course_name}))}
                            className={!row.course ? 'sms-import-blank' : undefined} />
                        </div>
                        <div className="col-12 col-sm-6">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon={REVIEW_FIELD_ICONS.intake} text="Intake" />
                          </label>
                          <SearchableSelect value={row.data.intake} onChange={e=>setPreviewRowData(idx, 'intake', e.target.value)}
                            placeholder={row.course ? '-- Select intake --' : '-- Select course first --'} disabled={!row.course}
                            options={intakeOptionsFor(intakes, row.course, { dateRange })}
                            className={!row.data.intake ? 'sms-import-blank' : undefined} />
                        </div>
                        {RF(row, idx, 'language', 'Language', 'text', { select:true, options: IMPORT_LANGUAGE_OPTIONS })}
                        {renderGuidingLanguage(row, idx)}
                        {RF(row, idx, 'enrollment_date', 'Enrolment Date', 'date')}
                        {RF(row, idx, 'education_level', 'Education Level', 'text')}
                        {RF(row, idx, 'employment_designation', 'Employment Designation', 'text')}
                        {RF(row, idx, 'employment_salary_range', 'Employment Salary Range', 'text')}
                        {RF(row, idx, 'next_of_kin', 'Next-of-Kin', 'text')}
                        {RF(row, idx, 'next_of_kin_contact', 'Next-of-Kin Contact', 'tel')}
                        {RF(row, idx, 'type_of_application', 'Type Of Application', 'text', { select:true, options: TYPE_OF_APPLICATION_OPTIONS })}
                        <div className="col-12 col-sm-6">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon={REVIEW_FIELD_ICONS.status} text="Status" />
                          </label>
                          <SearchableSelect value={row.data.status} onChange={e=>setPreviewRowData(idx, 'status', e.target.value)}
                            placeholder="-- Select status --"
                            options={IMPORT_STATUS_OPTIONS.map(v=>({value:v,label:v[0].toUpperCase()+v.slice(1)}))}
                            className={!row.data.status ? 'sms-import-blank' : undefined} />
                        </div>
                        <div className="col-12">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon="bi-bell" text="Notify via" />
                          </label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input type="checkbox" className="form-check-input" id={`pv-notify-email-${idx}`}
                                checked={!!row.data.notify_email} onChange={e=>setPreviewRowData(idx,'notify_email',e.target.checked)} />
                              <label className="form-check-label" htmlFor={`pv-notify-email-${idx}`} style={{fontSize:13}}>Email</label>
                            </div>
                            <div className="form-check">
                              <input type="checkbox" className="form-check-input" id={`pv-notify-whatsapp-${idx}`}
                                checked={!!row.data.notify_whatsapp} onChange={e=>setPreviewRowData(idx,'notify_whatsapp',e.target.checked)} />
                              <label className="form-check-label" htmlFor={`pv-notify-whatsapp-${idx}`} style={{fontSize:13}}>WhatsApp</label>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>
                            <RFLabel icon="bi-chat-square-text" text="Remarks" />
                          </label>
                          <textarea className="sms-form-control" rows={2} style={{resize:'vertical'}}
                            value={row.data.remarks||''} onChange={e=>setPreviewRowData(idx,'remarks',e.target.value)}
                            placeholder="Carried over from the file's Notes column, if any" />
                        </div>
                      </div>
                      </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="sms-btn sms-btn-outline" onClick={()=>setImportPreview(null)} disabled={importConfirming}>Cancel</button>
                <button type="button" className="sms-btn sms-btn-primary" onClick={handleConfirmImport}
                  disabled={previewHasBlanks || importConfirming || markedForImportCount === 0}
                  title={
                    markedForImportCount === 0 ? 'Mark at least one applicant to import' :
                    previewHasBlanks ? 'Fill in every highlighted field for the marked applicants first' : undefined
                  }>
                  {importConfirming ? <InlineSpinner/> : `Import ${markedForImportCount === importPreview.rows.filter(r=>!r.skip).length ? 'All' : `Marked (${markedForImportCount})`}`}
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal show={!!importResult} onClose={()=>setImportResult(null)} title="Import Results" size="lg">
        {importResult && (() => {
          const capacityCount  = importResult.capacityExceeded?.length || 0;
          const mismatchCount  = importResult.intakeMismatches?.length || 0;
          const noModulesCount = importResult.noModulesIntakes?.length || 0;
          const skippedCount   = importResult.skipReasons?.length || 0;
          const createdCount   = importResult.createdCredentials?.length || 0;
          const readyCount     = createdCount - capacityCount - mismatchCount;
          return (
            <>
              <div className="d-flex gap-2 flex-wrap mb-3">
                <span className="badge rounded-pill badge-paid" style={{fontSize:12}}><i className="bi bi-check-circle-fill" style={{marginRight:4}}/>{readyCount} created</span>
                {(capacityCount>0 || mismatchCount>0) && (
                  <span className="badge rounded-pill badge-late" style={{fontSize:12}}><i className="bi bi-exclamation-triangle-fill" style={{marginRight:4}}/>{capacityCount+mismatchCount} need a course</span>
                )}
                {skippedCount>0 && (
                  <span className="badge rounded-pill badge-void" style={{fontSize:12}}><i className="bi bi-x-circle-fill" style={{marginRight:4}}/>{skippedCount} skipped</span>
                )}
              </div>

              {capacityCount>0 && (
                <div className="d-flex align-items-center justify-content-between gap-2" style={{border:'1px solid var(--sms-bdr)',borderRadius:8,padding:'8px 12px',marginBottom:8}}>
                  <div style={{fontSize:12.5}}>{capacityCount} student(s) created without a course — their intake was full.</div>
                  <button type="button" className="sms-btn sms-btn-primary" style={{fontSize: 12,padding:'4px 10px',flexShrink:0}} onClick={openCapacityFromResult}>Resolve</button>
                </div>
              )}
              {mismatchCount>0 && (
                <div className="d-flex align-items-center justify-content-between gap-2" style={{border:'1px solid var(--sms-bdr)',borderRadius:8,padding:'8px 12px',marginBottom:8}}>
                  <div style={{fontSize:12.5}}>{mismatchCount} student(s) created without a course — their intake code didn't match any existing intake.</div>
                  <button type="button" className="sms-btn sms-btn-primary" style={{fontSize: 12,padding:'4px 10px',flexShrink:0}} onClick={openMismatchFromResult}>Resolve</button>
                </div>
              )}
              {noModulesCount>0 && (
                <div className="d-flex align-items-center justify-content-between gap-2" style={{border:'1px solid var(--sms-bdr)',borderRadius:8,padding:'8px 12px',marginBottom:8}}>
                  <div style={{fontSize:12.5}}>{noModulesCount} student(s) were enrolled, but their intake has no modules set up yet.</div>
                  <button type="button" className="sms-btn sms-btn-primary" style={{fontSize: 12,padding:'4px 10px',flexShrink:0}}
                    onClick={()=>{
                      // Multiple students can land in different intakes with no
                      // modules — open the first one straight away and hand the
                      // rest along so the Intakes page can flag them too.
                      const codes = [...new Set(importResult.noModulesIntakes.map(s=>s.intake_code))];
                      setImportResult(null);
                      navigate('/records/intakes', { state: { openModulesForIntake: codes[0], pendingModuleIntakes: codes.slice(1) } });
                    }}>Set up modules</button>
                </div>
              )}

              {skippedCount>0 && (
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--sms-txt2)',marginBottom:6}}>Skipped rows ({skippedCount}) — not created</div>
                  <div style={{maxHeight:180,overflowY:'auto',display:'flex',flexDirection:'column',gap:5}}>
                    {importResult.skipReasons.map((r,i) => (
                      <div key={i} style={{fontSize:12,color:'var(--sms-txt2)',borderLeft:'3px solid var(--sms-btn-outline-danger-txt)',paddingLeft:8}}>{r}</div>
                    ))}
                  </div>
                </div>
              )}

              {createdCount>0 && (
                <details>
                  <summary style={{fontSize:12,fontWeight:600,color:'var(--sms-txt2)',cursor:'pointer'}}>Created students & default passwords ({createdCount})</summary>
                  <div style={{maxHeight:200,overflowY:'auto',marginTop:8}}>
                    <table className="sms-table">
                      <thead><tr><th>Student ID</th><th>Email</th><th>Default Password</th></tr></thead>
                      <tbody>
                        {importResult.createdCredentials.map(c => (
                          <tr key={c.student_no}>
                            <td>{c.student_no}</td>
                            <td>{c.email}</td>
                            <td>{c.default_password}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          );
        })()}
        <div className="d-flex justify-content-end mt-3">
          <button type="button" className="sms-btn sms-btn-outline" onClick={()=>setImportResult(null)}>Close</button>
        </div>
      </Modal>
      <CapacityResolveModal show={showCapacityModal} onClose={()=>setShowCapacityModal(false)}
        title="Assign remaining students" rows={capacityLeftover}
        onResolved={key => setCapacityLeftover(rows => rows.filter(r => r.key !== key))}
        courses={courses.filter(c=>c.status==='Active')}
        intakesForCourse={courseCode => intakeOptionsFor(intakes, courseCode, { dateRange })}
        onAssign={assignLeftoverIntake} />
      <CapacityResolveModal show={showMismatchModal} onClose={()=>setShowMismatchModal(false)}
        title="Fix mismatched intakes" rows={mismatchLeftover}
        description={`${mismatchLeftover.length} student(s) were imported with an intake code that didn't match any existing intake. Choose the correct course and intake for each, then save.`}
        resolvedMessage="All mismatched intakes have been resolved."
        onResolved={key => setMismatchLeftover(rows => rows.filter(r => r.key !== key))}
        courses={courses.filter(c=>c.status==='Active')}
        intakesForCourse={courseCode => intakeOptionsFor(intakes, courseCode, { dateRange })}
        onAssign={assignLeftoverIntake} />
      {viewing && (
      <DetailPanel show={viewingOpen} onClose={()=>setViewingOpen(false)}
        title={`Student Details — ${viewing.name}`}
        header={(
          <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
            <ProfilePicture hasPicture={!!viewing.profile_picture} getUrl={`/students/${viewing.student_no}/picture`} size={72} />
            <div style={{display:'flex',gap:10,flex:'1 1 260px',flexWrap:'wrap'}}>
              <div className="sms-detail-field" style={{flex:'1 1 120px'}}>
                <div className="sms-detail-label">Student ID</div>
                <div className="sms-detail-value" style={{}}>{viewing.student_no}</div>
              </div>
              <div className="sms-detail-field" style={{flex:'1 1 160px'}}>
                <div className="sms-detail-label">Full Name</div>
                <div className="sms-detail-value">{viewing.name}</div>
              </div>
            </div>
          </div>
        )}
        fields={[
        { label: 'Title',             value: viewing.title || '—' },
        { label: 'Full Name',         value: viewing.name },
        { label: 'Identity Type',     value: viewing.identity_type || '—' },
        { label: 'Identity Value',    value: viewing.nric },
        { label: 'Gender',            value: displayTitleCase(viewing.gender) },
        { label: 'Date of Birth',     value: viewing.date_of_birth ? fmtDate(viewing.date_of_birth) : '—' },
        { label: 'Citizenship',       value: displayTitleCase(viewing.citizenship) },
        { label: 'Nationality',       value: viewing.nationality },
        { label: 'Race',              value: viewing.race || '—' },
        { label: 'Email',             value: viewing.email },
        { label: 'Contact No. Country Code', value: viewing.contact_no_country_code ? `+${viewing.contact_no_country_code}` : '—' },
        { label: 'Phone',             value: viewing.phone ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {viewing.phone}
            <CopyPhoneButton onCopy={() => copyPhone(viewing.phone)} />
          </span>
        ) : '—' },
        { label: 'Language',          value: viewing.language },
        { label: 'Guiding Language',  value: viewing.guiding_language },
        { label: 'Education Level',   value: viewing.education_level || '—' },
        { label: 'Employment Designation', value: viewing.employment_designation || '—' },
        { label: 'Employment Salary Range', value: viewing.employment_salary_range || '—' },
        { label: 'Type Of Application', value: viewing.type_of_application || '—' },
        { label: 'Next-of-Kin',       value: viewing.next_of_kin || '—' },
        { label: 'Next-of-Kin Contact', value: viewing.next_of_kin_contact || '—' },
        { label: 'Enrolment Date',    value: fmtDate(viewing.enrollment_date) },
        { label: 'Status',            value: <Badge value={viewing.status} map={statusBadgeMap}/> },
        { label: 'Notify via',        value: [viewing.notify_email && 'Email', viewing.notify_whatsapp && 'WhatsApp'].filter(Boolean).join(', ') || 'None' },
        { label: 'Remarks',           value: viewing.remarks || '—' },
      ]}>
            <div className="mt-3">
              <FileAttachments module="students" recordId={viewing.student_no} canUpload={false} canDelete={false} />
            </div>
            <div style={{ borderTop: '1px solid var(--sms-divider)', marginTop: 16, paddingTop: 16 }}>
              <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>
                <i className="bi bi-mortarboard" /> Courses
              </div>
              {intakesLoading ? <Spinner minHeight="200px" /> : (
                <div className="d-flex flex-column gap-2">
                  {studentCourses.map(row => (
                    <div key={row.id}>
                      <div className="d-flex align-items-start gap-2" style={{
                        border: '1px solid var(--sms-bdr)', borderRadius: 9, padding: '8px 10px', background: 'var(--sms-sur2)',
                        flexWrap: 'wrap', rowGap: 6,
                      }}>
                        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>
                            {courses.find(c => c.course_code === row.course_code)?.course_name || 'Unknown course'}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 3 }}>
                            <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>{row.intake_code}</span>
                            <span className={`badge rounded-pill ${INTAKE_STATUS_BADGE[row.status]}`} style={{ fontSize: 12, flexShrink: 0 }}>
                              {INTAKE_STATUS_LABEL[row.status] || row.status}
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>
                              {row.status === 'transferred' && row.transferred_to_id ? 'Transferred out' : fmtDate(row.enrolled_at)}
                            </span>
                            {intakeRates[row.intake_code] != null && (
                              <span className={`badge rounded-pill ${pctBadgeClass(intakeRates[row.intake_code])}`}
                                style={{ fontSize: 12, flexShrink: 0 }} title="Attendance rate for this intake">
                                {fmtPct(intakeRates[row.intake_code])}
                              </span>
                            )}
                            <button type="button" className="sms-link-btn" onClick={() => toggleHistory(row)}>
                              {historyFor === row.id ? 'Hide history' : 'View history'}
                            </button>
                          </div>
                        </div>
                        {can('students', 'update') && (
                          <div className="d-flex gap-1" style={{ flexShrink: 0, marginLeft: 'auto', marginTop: 2 }}>
                            {/* Always viewable regardless of course/student status — only adding a
                                module (inside the modal) is restricted to registered/active courses. */}
                            <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '3px 7px', fontSize: 12 }}
                              disabled={intakeActionBusy} title="View modules" onClick={() => openModules(row)}>
                              <i className="bi bi-journal-text" />
                            </button>
                            {viewing.status === 'active' && row.status === 'registered' && (
                              <>
                                {/* Mark active is hidden here, not removed — handleMarkActive/markActive
                                    endpoint stay intact. Registered -> Active is meant to happen by
                                    issuing a receipt (see handleEnroll), not this manual override. */}
                                <button type="button" className="sms-btn sms-btn-outline-danger" style={{ padding: '3px 7px', fontSize: 12 }}
                                  disabled={intakeActionBusy} title="Mark withdrawn" onClick={() => handleEndEnrollment(row, 'withdrawn')}>
                                  <i className="bi bi-x-lg" />
                                </button>
                              </>
                            )}
                            {viewing.status === 'active' && row.status === 'active' && (
                              <>
                                <button type="button" className="sms-btn sms-btn-outline-info" style={{ padding: '3px 7px', fontSize: 12 }}
                                  disabled={intakeActionBusy} title="Transfer to another intake" onClick={() => openTransfer(row)}>
                                  <i className="bi bi-arrow-left-right" />
                                </button>
                                <button type="button" className="sms-btn sms-btn-outline-success" style={{ padding: '3px 7px', fontSize: 12 }}
                                  disabled={intakeActionBusy} title="Mark completed" onClick={() => handleEndEnrollment(row, 'completed')}>
                                  <i className="bi bi-check-lg" />
                                </button>
                                <button type="button" className="sms-btn sms-btn-outline-danger" style={{ padding: '3px 7px', fontSize: 12 }}
                                  disabled={intakeActionBusy} title="Mark withdrawn" onClick={() => handleEndEnrollment(row, 'withdrawn')}>
                                  <i className="bi bi-x-lg" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {historyFor === row.id && (
                        <div style={{ border: '1px solid var(--sms-bdr)', borderTop: 'none', borderRadius: '0 0 9px 9px', padding: '8px 12px', background: 'var(--sms-sur)' }}>
                          {historyLoading ? <Spinner minHeight="200px" /> : historyRows.length ? (
                            <div className="d-flex flex-column gap-1">
                              {historyRows.map(h => (
                                <div key={h.id} style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                                  <span style={{ color: 'var(--sms-txt3)' }}>{fmtDateTime(h.changed_at)}</span>
                                  {' — '}
                                  {h.old_status
                                    ? `${INTAKE_STATUS_LABEL[h.old_status] || h.old_status} → ${INTAKE_STATUS_LABEL[h.new_status] || h.new_status}`
                                    : `enrolled, status set to ${INTAKE_STATUS_LABEL[h.new_status] || h.new_status}`}
                                  {h.changed_by_name && ` (by ${h.changed_by_name})`}
                                </div>
                              ))}
                            </div>
                          ) : <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>No history recorded</span>}
                        </div>
                      )}
                    </div>
                  ))}
                  {!studentCourses.length && <EmptyState message="No course enrolments recorded" icon="bi-mortarboard" />}

                  {can('students', 'update') && viewing.status === 'active' && (
                    enrollOpen ? (
                      <div style={{ border: '1.5px dashed var(--sms-bdr)', borderRadius: 9, padding: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 12, color: 'var(--sms-txt3)', display: 'block', marginBottom: 3 }}>Course</label>
                            <SearchableSelect value={enrollForm.course_code}
                              onChange={e => setEnrollForm(f => ({ ...f, course_code: e.target.value, intake_code: '' }))}
                              placeholder="-- Select course --"
                              options={courses.filter(c => c.status === 'Active').map(c => ({ value: c.course_code, label: c.course_name }))} />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: 'var(--sms-txt3)', display: 'block', marginBottom: 3 }}>Intake</label>
                            <SearchableSelect value={enrollForm.intake_code}
                              onChange={e => setEnrollForm(f => ({ ...f, intake_code: e.target.value }))}
                              placeholder={enrollForm.course_code ? '-- Select intake --' : '-- Select course first --'}
                              disabled={!enrollForm.course_code}
                              options={intakeOptionsFor(intakes, enrollForm.course_code, { dateRange })} />
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
                          <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>
                            Enrols as Registered, dated today — issue a receipt for this course to mark it Active.
                          </span>
                          <div style={{ marginLeft: 'auto' }} className="d-flex gap-2">
                            <button type="button" className="sms-btn sms-btn-primary" style={{ fontSize: 12 }}
                              disabled={intakeActionBusy || !enrollForm.course_code || !enrollForm.intake_code}
                              onClick={handleEnroll}>Enrol</button>
                            <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12 }}
                              onClick={() => setEnrollOpen(false)}>Cancel</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="sms-btn sms-btn-outline w-100 d-flex align-items-center justify-content-center gap-2"
                        style={{ fontSize: 12.5, borderStyle: 'dashed' }} onClick={() => setEnrollOpen(true)}>
                        <i className="bi bi-plus-lg" /> Enrol in another course
                      </button>
                    )
                  )}
                </div>
              )}
            </div>

            {can('receipts', 'view') && Section('payments', 'Payment', data => {
              if (!data || !data.length) return <EmptyState message="No payments recorded" icon="bi-receipt" />;
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table className="sms-table">
                    <thead>
                      <tr>
                        <th>Receipt No</th>
                        <th>Intake</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th>Method</th>
                        <th>Date</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map(row => (
                        <tr key={row.id} style={{ opacity: row.status === 'void' ? 0.55 : 1 }}>
                          <td>{row.receipt_no}</td>
                          <td>{row.intake_code || '—'}</td>
                          <td style={{ textAlign: 'right', textDecoration: row.status === 'void' ? 'line-through' : 'none' }}>{fmtAmount(row.amount)}</td>
                          <td>{PAYMENT_METHOD_LABELS[row.payment_method] || row.payment_method}</td>
                          <td>{fmtDate(row.payment_date)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge rounded-pill ${row.status === 'void' ? 'badge-void' : 'badge-paid'}`} style={{ fontSize: 12 }}>
                              {RECEIPT_STATUS_LABELS[row.status] || row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}

            {can('attendance', 'view') && Section('attendance', 'Attendance', data => {
              const records = data?.records || [];
              const summary = data?.summary;
              const totalSessions = Number(summary?.total_sessions) || 0;
              const present = Number(summary?.present) || 0;
              const late = Number(summary?.late) || 0;
              const absent = Number(summary?.absent) || 0;
              const mc = Number(summary?.mc) || 0;
              const pct = totalSessions ? Math.round((((present + late + mc) - absent) / totalSessions) * 100) : 0;

              return (
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                    <i className="bi bi-info-circle" aria-hidden="true" />
                    <span>Only ended sessions are included in this attendance history.</span>
                  </div>
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                      <label style={{ fontSize: 12, color: 'var(--sms-txt3)', display: 'block', marginBottom: 3 }}>Intake</label>
                      <SearchableSelect value={attendanceIntake} onChange={e => setAttendanceIntake(e.target.value)}
                        placeholder="-- Select intake --"
                        options={studentCourses.map(r => ({ value: r.intake_code, label: r.intake_code }))} />
                    </div>
                    <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                      <label style={{ fontSize: 12, color: 'var(--sms-txt3)', display: 'block', marginBottom: 3 }}>Module</label>
                      <SearchableSelect value={attendanceModule} onChange={e => setAttendanceModule(e.target.value)}
                        placeholder={attendanceIntake ? '-- All modules --' : 'Select an intake first'} disabled={!attendanceIntake}
                        options={attendanceModules.map(m => ({ value: m.module_code, label: m.module_name }))} />
                    </div>
                  </div>

                  {!totalSessions ? (
                    <EmptyState message="No ended attendance records for this selection" icon="bi-calendar" />
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                          Present: {present} · Late: {late} · Absent: {absent} · {totalSessions} ended sessions
                        </span>
                        <span className={`badge rounded-pill ${pctBadgeClass(pct)}`} style={{ fontSize: 13 }}>{fmtPct(pct)}</span>
                      </div>
                      <div className="sms-progress">
                        <div className="sms-progress-fill" style={{ width: `${pct}%`, background: pctColorVar(pct), transition: 'width .4s' }} />
                      </div>
                    </>
                  )}

                  {records.length > 0 && (
                    <div className="mt-3" style={{ overflowX: 'auto' }}>
                      <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>
                        <i className="bi bi-calendar-check" aria-hidden="true" /> Ended session history
                      </div>
                      <table className="sms-table">
                        <thead>
                          <tr>
                            <th>Session</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Time</th>
                            <th>Venue</th>
                            <th>Trainer</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map(record => (
                            <tr key={record.id}>
                              <td>{fmtSessionName(record) || '—'}</td>
                              <td>{sessionTypeKey(record.session_type)
                                ? <Badge value={sessionTypeKey(record.session_type)} map={sessionTypeBadgeMap} />
                                : '—'}</td>
                              <td>{fmtDate(record.session_date)}</td>
                              <td>{fmtSessionTime(record.start_time)}{record.end_time ? ` – ${fmtSessionTime(record.end_time)}` : ''}</td>
                              <td>{record.venue_name || '—'}</td>
                              <td>{record.trainer_name || '—'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <Badge value={record.status} map={attendanceBadgeMap} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}

            {can('academic_performance', 'view') && Section('academic', 'Academic Performance', data => {
              if (!data || !data.length) return <EmptyState message="No academic records yet" icon="bi-graph-up" />;
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table className="sms-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Intake</th>
                        <th style={{ textAlign: 'center' }}>Assessments</th>
                        <th style={{ textAlign: 'center' }}>Avg %</th>
                        <th style={{ textAlign: 'center' }}>Highest</th>
                        <th style={{ textAlign: 'center' }}>Lowest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, i) => {
                        const avg = parseFloat(row.avg_pct) || 0;
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{row.subject}</td>
                            <td>{row.intake_code || '—'}</td>
                            <td style={{ textAlign: 'center', fontSize: 13 }}>{row.assessments}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`badge rounded-pill ${pctBadgeClass(avg)}`} style={{ fontSize: 12 }}>{fmtPct(avg)}</span>
                            </td>
                            <td style={{ textAlign: 'center', fontSize: 13 }}>{row.highest}</td>
                            <td style={{ textAlign: 'center', fontSize: 13 }}>{row.lowest}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}

            {can('academic_performance', 'view') && Section('mcq', 'Assessment Result', data => {
              if (!data || !data.length) return <EmptyState message="No Assessment Results yet" icon="bi-list-check" />;
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table className="sms-table">
                    <thead>
                      <tr>
                        <th>Assessment</th>
                        <th style={{ textAlign: 'center' }}>Type</th>
                        <th style={{ textAlign: 'center' }}>Attempt</th>
                        <th style={{ textAlign: 'center' }}>Score</th>
                        <th style={{ textAlign: 'center' }}>%</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, i) => {
                        const pct = parseFloat(row.percentage) || 0;
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{row.assessment_name}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`badge rounded-pill ${row.type === 'Exam' ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>
                                {row.type}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', fontSize: 13 }}>{row.attempt}</td>
                            <td style={{ textAlign: 'center', fontSize: 13 }}>{row.correct_count}/{row.max_score}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`badge rounded-pill ${pctBadgeClass(pct)}`} style={{ fontSize: 12 }}>{fmtPct(pct)}</span>
                            </td>
                            <td>{fmtDate(row.submitted_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
      </DetailPanel>
      )}
      </div>

      <Modal show={!!transferTarget} size="md" title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>Transfer Enrolment</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 2 }}>
            {viewing?.name} — moves the student to a new intake and updates their status
          </div>
        </div>
      } onClose={() => setTransferTarget(null)}>
        {transferTarget && (
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start gap-2">
              <div style={{ flex: 1, minWidth: 0, border: '1px solid var(--sms-bdr)', borderRadius: 8, padding: '8px 10px', background: 'var(--sms-sur2)' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase' }}>From</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{transferTarget.intake_code}</div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 2 }}>
                  {fmtDate(transferTarget.soc_date)} – {fmtDate(transferTarget.eoc_date)}
                </div>
              </div>
              <div className="d-flex justify-content-center align-items-center">
                <i className="bi bi-arrow-down d-sm-none" style={{ color: 'var(--sms-txt3)' }} />
                <i className="bi bi-arrow-right d-none d-sm-block" style={{ color: 'var(--sms-txt3)', marginTop: 10 }} />
              </div>
              <div style={{ flex: 1.4, minWidth: 0, borderRadius: 8, padding: '8px 10px', border: '1px solid var(--sms-bdr)' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase' }}>Course</div>
                <SearchableSelect value={transferForm.course_code}
                  onChange={e => { setTransferForm(f => ({ ...f, course_code: e.target.value, to_intake_code: '' })); setTransferErrors({}); }}
                  placeholder="-- Select course --"
                  options={courses.filter(c => c.status === 'Active').map(c => ({ value: c.course_code, label: c.course_name }))} />
              </div>
              <div className="d-flex justify-content-center align-items-center">
                <i className="bi bi-arrow-down d-sm-none" style={{ color: 'var(--sms-txt3)' }} />
                <i className="bi bi-arrow-right d-none d-sm-block" style={{ color: 'var(--sms-txt3)', marginTop: 10 }} />
              </div>
              <div style={{
                flex: 1, minWidth: 0, borderRadius: 8, padding: '8px 10px',
                border: `1px solid ${transferErrors.to_intake_code ? '#b91c1c' : 'var(--sms-acc)'}`,
                background: 'var(--sms-acc-bg)',
              }}>
                <div style={{ fontSize: 12, color: transferErrors.to_intake_code ? '#b91c1c' : 'var(--sms-acc)', textTransform: 'uppercase' }}>To</div>
                <SearchableSelect value={transferForm.to_intake_code}
                  onChange={e => { setTransferForm(f => ({ ...f, to_intake_code: e.target.value })); setTransferErrors({}); }}
                  placeholder={transferForm.course_code ? '-- Select intake --' : '-- Select course first --'}
                  disabled={!transferForm.course_code}
                  options={intakeOptionsFor(intakes, transferForm.course_code, { excludeIntakeCode: transferTarget.intake_code, dateRange })} />
              </div>
            </div>
            {transferErrors.to_intake_code && (
              <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: -8 }}>Please select a destination intake.</div>
            )}

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Transfer date</label>
              <input type="date" className="sms-form-control" style={{ fontSize: 13 }}
                max={new Date().toISOString().slice(0, 10)}
                value={transferForm.transfer_date} onChange={e => setTransferForm(f => ({ ...f, transfer_date: e.target.value }))} />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Attendance</label>
              <div className="d-flex flex-column gap-2">
                {[
                  { value: 'from_date', title: `Move sessions from ${transferForm.transfer_date ? fmtDate(transferForm.transfer_date) : 'transfer date'} onward`, sub: 'Recommended. Earlier sessions stay recorded under the old intake as history.' },
                  { value: 'all',       title: 'Move all attendance history', sub: `Every session record for this student moves to ${transferForm.to_intake_code || 'the new intake'}, including past ones.` },
                  { value: 'none',      title: `Leave attendance with ${transferTarget.intake_code}`, sub: 'No records move. Only the enrolment itself is transferred.' },
                ].map(opt => (
                  <label key={opt.value} className="d-flex align-items-start gap-2" style={{
                    border: `1.5px solid ${transferForm.attendance_migration === opt.value ? 'var(--sms-acc)' : 'var(--sms-bdr)'}`,
                    background: transferForm.attendance_migration === opt.value ? 'var(--sms-acc-bg)' : 'transparent',
                    borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
                  }}>
                    <input type="radio" name="attendance_migration" style={{ marginTop: 3 }}
                      checked={transferForm.attendance_migration === opt.value}
                      onChange={() => setTransferForm(f => ({ ...f, attendance_migration: opt.value }))} />
                    <span>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{opt.title}</span>
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--sms-txt3)', lineHeight: 1.4 }}>{opt.sub}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setTransferTarget(null)}>Cancel</button>
              <button type="button" className="sms-btn sms-btn-primary" disabled={intakeActionBusy || !transferForm.to_intake_code || !transferForm.transfer_date}
                onClick={handleTransfer}>Confirm Transfer</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal show={!!moduleTarget} size="sm" title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>
            {moduleTarget && ['registered', 'active'].includes(moduleTarget.status) ? 'Add Module' : 'Modules'}
          </div>
          {moduleTarget && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 2 }}>{moduleTarget.intake_code}</div>}
        </div>
      } onClose={() => setModuleTarget(null)}>
        {modulesLoading ? <Spinner minHeight="200px" /> : (
          <div className="d-flex flex-column gap-2">
            {!enrolmentModules.length && <EmptyState message="No modules assigned to this intake" icon="bi-journal-x" />}
            {moduleTarget && !['registered', 'active'].includes(moduleTarget.status) && enrolmentModules.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginBottom: 2 }}>
                <i className="bi bi-info-circle" style={{ marginRight: 4 }} />
                This course is {INTAKE_STATUS_LABEL[moduleTarget.status] || moduleTarget.status} — modules can no longer be added.
              </div>
            )}
            {enrolmentModules.map(m => (
              <div key={m.module_code} className="d-flex align-items-center justify-content-between gap-2"
                style={{ border: '1px solid var(--sms-bdr)', borderRadius: 8, padding: '7px 10px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{m.module_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>{m.module_code}</div>
                </div>
                {m.status ? (
                  <span className={`badge rounded-pill ${m.status === 'current' ? 'badge-active' : m.status === 'passed' ? 'badge-present' : m.status === 'failed' ? 'badge-at-risk' : m.status === 'withdrawn' ? 'badge-absent' : 'badge-inactive'}`} style={{ fontSize: 12, flexShrink: 0 }}>
                    {m.status[0].toUpperCase() + m.status.slice(1)}
                  </span>
                ) : moduleTarget && ['registered', 'active'].includes(moduleTarget.status) ? (
                  <button type="button" className="sms-btn sms-btn-outline-success" style={{ padding: '3px 9px', fontSize: 12, flexShrink: 0 }}
                    disabled={addingModule === m.module_code} onClick={() => handleAddModule(m.module_code)}>
                    {addingModule === m.module_code ? <InlineSpinner /> : 'Add'}
                  </button>
                ) : (
                  <span className="badge rounded-pill badge-inactive" style={{ fontSize: 12, flexShrink: 0 }}>Not added</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
